/*
 * The crew. A team of multiplayer cursors, named after the stack, builds the
 * page live, Figma style: rulers and guides, boxes drawn with size badges,
 * typed text, pen-tool lines and option-drag duplicates.
 *
 * Two runs share one engine:
 * - the intro (startIntro): on first load the crew orbits the logo while the
 *   page loads, bursts out, builds the header and hero, then hands the page
 *   over by flocking to the visitor's pointer;
 * - the Work section (armWork): the section stays blank until it scrolls into
 *   view, then the crew streams in and builds it as the visitor scrolls.
 *
 * The idea comes from Joel Califa's Swarm (califa.github.io/swarm-site) and
 * github.com/DhvanilPatel/swarm-cursors. This is a separate implementation
 * written for this site. It reveals the real DOM in place with clip-paths and
 * draws all tooling on one canvas, so nothing is cloned or swapped at the end.
 */

const KEY = 'portfolio-intro';
const WORK_KEY = 'portfolio-build-work';
const ABOUT_KEY = 'portfolio-build-about';
const JOURNEY_KEY = 'portfolio-build-journey';
const CONTACT_KEY = 'portfolio-build-contact';
const GUIDE = '#ff3d55';
const CREW = [
  ['React', '#61DAFB'], ['AWS', '#FF9900'], ['Angular', '#E35BFF'], ['Node.js', '#6CC24A'],
  ['TypeScript', '#5B9DF2'], ['Linux', '#FCC624'], ['WebRTC', '#FF5468'], ['Supabase', '#3ECF8E'],
  ['PM2', '#A47DFF'], ['Next.js', '#F2EFEB'], ['Git', '#F76B45'], ['GSAP', '#A3E635'],
  ['Docker', '#38A8F5'], ['Firebase', '#FFB938'],
];
const S_PATH = 'M46.5 20.5C43.6 15.4 38.6 13 32.4 13 24.2 13 18.5 17.2 18.5 23.6c0 14.7 27.5 8.6 27.5 21.8 0 6.6-5.6 10.6-13.6 10.6-6.9 0-12.2-2.9-15.2-8';
const ARROW = 'M3 3L19.5 9.8 11.7 12.1 9.6 20Z';
const SHAPES = {
  arrow: { d: ARROW, hx: 3, hy: 3, fill: 1 },
  dup: { d: ARROW, hx: 3, hy: 3, fill: 1, plus: 1 },
  cross: { d: 'M12 4.5V19.5M4.5 12H19.5', hx: 12, hy: 12 },
  ibeam: { d: 'M9.4 4.6C11 4.6 12 5.2 12 6.8 12 5.2 13 4.6 14.6 4.6M12 6.8V17.2M9.4 19.4C11 19.4 12 18.8 12 17.2 12 18.8 13 19.4 14.6 19.4', hx: 12, hy: 12 },
  pen: { d: 'M3 3L12.8 6.4 17.6 11.2 11.2 17.6 6.4 12.8ZM15.4 13.4L20.6 18.6 18.6 20.6 13.4 15.4Z', hx: 3, hy: 3, fill: 1, nib: 1 },
  ew: { d: 'M2.6 12L7 7.6V10.4H17V7.6L21.4 12 17 16.4V13.6H7V16.4Z', hx: 12, hy: 12, fill: 1 },
  ns: { d: 'M12 2.6L16.4 7H13.6V17H16.4L12 21.4 7.6 17H10.4V7H7.6Z', hx: 12, hy: 12, fill: 1 },
  // Rotate: a quarter arc with an arrowhead at each end.
  rot: { d: 'M6 15.5A9.5 9.5 0 0 1 15.5 6M12.6 3.6L15.5 6 12.8 8.6M3.6 12.6L6 15.5 8.6 12.8', hx: 10, hy: 10 },
};
// Turns the arrow so its tip leads the direction of travel while flocking.
const TURN = 2.335;
const ICON = `url("data:image/svg+xml,${encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="#56565e" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"><rect x="3.5" y="4.5" width="17" height="15" rx="2.5"/><circle cx="9" cy="10" r="1.7"/><path d="M4.5 17.5l5-4.5 3.5 3 2.5-2 4 3.5"/></svg>')}")`;

const STYLE = `.swarm-layer{position:fixed;inset:0;z-index:400;touch-action:none;-webkit-user-select:none;user-select:none}
.swarm-layer.swarm-pass{pointer-events:none;touch-action:auto}
.swarm-layer canvas{position:absolute;left:0;top:0}
.swarm-pill{position:fixed;left:50%;bottom:calc(16px + env(safe-area-inset-bottom,0px));z-index:401;display:flex;align-items:center;gap:10px;height:36px;padding:0 6px 0 9px;border-radius:12px;background:rgba(18,18,20,.88);border:1px solid #2a2a2f;box-shadow:0 12px 32px rgba(0,0,0,.5);color:#cfcac6;font:600 11px/1 Manrope,system-ui,sans-serif;white-space:nowrap;-webkit-backdrop-filter:blur(12px);backdrop-filter:blur(12px);transform:translateX(-50%);opacity:0;translate:0 12px;transition:opacity .45s ease,translate .6s cubic-bezier(.22,1,.36,1)}
.swarm-pill.on{opacity:1;translate:0 0}
.swarm-pill.on.off{opacity:0;translate:0 10px}
.swarm-pill b{color:#fff;font-weight:800}
.swarm-faces{display:flex}
.swarm-faces i{width:15px;height:15px;border-radius:50%;border:2px solid #121214;margin-left:-6px}
.swarm-faces i:first-child{margin-left:0}
.swarm-sep{width:1px;height:14px;background:#2c2c31}
.swarm-count{min-width:84px;font-variant-numeric:tabular-nums;color:#a7a29e}
.swarm-live{display:inline-block;width:6px;height:6px;border-radius:50%;background:#e32b3c;margin-right:7px;vertical-align:1px;box-shadow:0 0 0 3px rgba(227,43,60,.25)}
.swarm-skip{appearance:none;-webkit-appearance:none;border:0;background:transparent;cursor:pointer;display:flex;align-items:center;gap:6px;height:26px;padding:0 8px;border-radius:8px;color:#cfcac6;font:600 11px/1 Manrope,system-ui,sans-serif}
.swarm-skip:hover,.swarm-skip:focus-visible{background:#25252a;color:#fff;outline:none}
.swarm-skip kbd{font:600 9px/1 Manrope,system-ui,sans-serif;padding:3px 4px;border-radius:4px;background:#26262c;color:#8f8a86}
@media (hover:none){.swarm-skip kbd{display:none}}
@media (max-width:600px){.swarm-pill{gap:8px;height:32px;font-size:10px}.swarm-count{min-width:70px}.swarm-skip{font-size:10px}}`;

const clamp = (v, a, b) => (v < a ? a : v > b ? b : v);
const lerp = (a, b, t) => a + (b - a) * t;
const rnd = (a, b) => a + Math.random() * (b - a);
const linear = t => t;
const human = t => t * t * t * (t * (6 * t - 15) + 10);
const out3 = t => 1 - (1 - t) ** 3;
const inOut3 = t => (t < 0.5 ? 4 * t * t * t : 1 - (-2 * t + 2) ** 3 / 2);
const inOutSine = t => (1 - Math.cos(Math.PI * t)) / 2;
const outBack = t => 1 + 2.4 * (t - 1) ** 3 + 1.4 * (t - 1) ** 2;
const wrap = r => Math.atan2(Math.sin(r), Math.cos(r));
const lum = hex => {
  const n = parseInt(hex.slice(1), 16);
  const c = [n >> 16, (n >> 8) & 255, n & 255].map(v => ((v /= 255) <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4));
  return 0.2126 * c[0] + 0.7152 * c[1] + 0.0722 * c[2];
};
const rr = (x, l, t, w, h, r) => {
  r = Math.min(r, w / 2, h / 2);
  x.beginPath(); x.moveTo(l + r, t);
  x.arcTo(l + w, t, l + w, t + h, r); x.arcTo(l + w, t + h, l, t + h, r);
  x.arcTo(l, t + h, l, t, r); x.arcTo(l, t, l + w, t, r); x.closePath();
};
const $ = (s, r = document) => r.querySelector(s);
const $$ = (s, r = document) => [...r.querySelectorAll(s)];

// The visitor's pointer, tracked for the whole page (screen coordinates). A build
// ends with the crew sinking into it, and the next build starts by bursting out of it.
const pointer = { x: 0, y: 0, mouse: false, touch: null };
if (typeof window !== 'undefined') {
  const track = e => {
    if (e.pointerType === 'mouse') { pointer.x = e.clientX; pointer.y = e.clientY; pointer.mouse = true; }
    else pointer.touch = { x: e.clientX, y: e.clientY };
  };
  addEventListener('pointermove', track, { passive: true });
  addEventListener('pointerdown', track, { passive: true });
}
const rendered = el => {
  if (!el) return false;
  const r = el.getBoundingClientRect();
  return r.width >= 1 && r.height >= 1 && getComputedStyle(el).visibility !== 'hidden';
};

// Generator helpers. Every script yields once per frame and receives the
// elapsed (virtual) milliseconds, so a speed change applies to everything.
function* wait(ms) { let t = 0; while (t < ms) t += yield; }
function* until(test) { while (!test()) yield; }
function* tween(ms, fn, e = human) {
  if (ms <= 0) { fn(1); return; }
  let t = 0; fn(0);
  while (t < ms) { t += yield; fn(e(Math.min(1, t / ms))); }
}

// Every inline style the crew touches is recorded, so the page ends exactly as React left it.
function keeper() {
  const saved = new Map();
  const put = (el, p, v) => {
    let r = saved.get(el);
    if (!r) saved.set(el, (r = {}));
    if (!(p in r)) r[p] = el.style.getPropertyValue(p);
    el.style.setProperty(p, v);
  };
  const back = (el, p) => {
    const r = saved.get(el);
    if (!r || !(p in r)) return;
    if (r[p]) el.style.setProperty(p, r[p]); else el.style.removeProperty(p);
    delete r[p];
  };
  const restoreAll = () => { for (const [el, r] of saved) for (const p in r) back(el, p); saved.clear(); };
  return { saved, put, back, restoreAll };
}

// One job per layer. Lower prio goes first; distance breaks ties.
function makeJobs() {
  const jobs = [];
  const job = (kind, el, prio, o = {}) => { const j = { kind, el, prio, deps: [], done: false, taken: false, own: [], ...o }; jobs.push(j); return j; };
  return { jobs, job };
}

function planHero(hero, header, job, visible, vh, small) {
  const at = (kind, sel, prio, o) => { const el = $(sel, hero); return visible(el) ? job(kind, el, prio, o) : null; };
  const wordmark = $('.site-header .wordmark');
  if (visible(wordmark)) job('type', wordmark, 1);
  const nav = $$('.desktop-nav a').filter(visible);
  if (nav.length) {
    const master = job('type', nav[0], 1.5);
    for (const a of nav.slice(1)) job('dup', a, 2.1, { src: nav[0], deps: [master] });
  }
  for (const s of ['.motion-toggle', '.header-contact']) { const el = $(s); if (visible(el)) job('type', el, 3); }
  const menu = $('.menu-toggle');
  if (visible(menu)) job('pop', menu, 2.6);
  job('line', header, 1.2, { side: 'bottom' });

  at('type', '.hero-top > span:first-child', 1.4);
  at('type', '.availability', 2.4);
  at('grow', '.hero-title', 0);
  const media = at('media', '.hero-portrait', 0);
  at('write', '.hero-intro .handwriting', 0.9);
  at('type', '.hero-intro h1', 0, { slow: true });
  at('type', '.hero-role', 1);
  at('type', '.hero-description', 1);
  at('button', '.primary-link', 1.6);
  at('type', '.hero-location', 2.8);
  at('shape', '.hero-manifesto .crosshair', 2.4);
  at('type', '.hero-manifesto p', 2.6);
  const rows = $$('.hero-stats > div', hero).filter(visible);
  if (rows.length) {
    // Build the first stat (number and label in parallel), then option-drag copies of it.
    const parts = r => [r.querySelector('.display'), r.querySelector(':scope > p, :scope > a')];
    const [num, lab] = parts(rows[0]);
    const deps = [];
    if (visible(num)) deps.push(job('type', num, 0.9, { slow: true }));
    if (visible(lab)) deps.push(job('type', lab, 1));
    job('line', rows[0], 1.5, { side: 'bottom' });
    for (const r of rows.slice(1)) job('duprow', r, 1.6, { src: rows[0], parts: parts(r), srcParts: parts(rows[0]), deps });
  }
  at('type', '.hero-stats > a', 3);
  at('line', '.hero-bottom', 1.2, { side: 'top' });
  at('type', '.reel-button', 2.6);
  at('type', '.hero-scroll', 3.2);
  at('type', '.edition', 3.2);
  if (hero.getBoundingClientRect().bottom < vh - 3) job('line', hero, 3.4, { side: 'bottom' });
  const intro = $('.hero-intro', hero), bottom = $('.hero-bottom', hero);
  if (visible(intro)) job('guide', null, 0.4, { axis: 'x', at: box => box(intro).l });
  if (visible(bottom) && !small) job('guide', null, 0.5, { axis: 'y', at: box => box(bottom).t });
  return media;
}

function planWork(section, job) {
  const at = (kind, sel, prio, o) => { const el = $(sel, section); return rendered(el) ? job(kind, el, prio, o) : null; };
  const heading = at('type', '.section-heading h2', 0.4, { slow: true });
  at('type', '.section-heading .eyebrow', 0.5);
  at('type', '.section-heading .text-link', 1.2);
  const bar = $('.portfolio-toolbar', section);
  if (rendered(bar)) { job('line', bar, 0.8, { side: 'top' }); job('line', bar, 1, { side: 'bottom' }); }
  // Filters: type the first, then option-drag it into the others.
  const filters = $$('.filters button', section).filter(rendered);
  if (filters.length) {
    const master = job('type', filters[0], 0.9);
    for (const f of filters.slice(1)) job('dup', f, 1.4, { src: filters[0], deps: [master] });
  }
  at('type', '.search-box', 1.6);
  // Cards: draw the first frame, sweep its picture in, type its caption,
  // then option-drag copies of the card into the other slots and fill them in.
  const cards = $$('.project-grid > .project-card', section).filter(rendered);
  if (cards.length) {
    const parts = c => ({
      frame: $('.project-open', c), pic: $('.project-picture', c), badge: $('.project-type', c), cap: $('.project-caption', c),
      num: $('.project-number', c), title: $('.project-caption h3', c), cat: $('.project-category', c), arrow: $('.project-caption > svg', c),
    });
    const p0 = parts(cards[0]);
    const frame0 = job('frame', p0.frame, 0.2, { p: p0 });
    job('sweep', p0.frame, 0.9, { p: p0, deps: [frame0] });
    for (const [k, prio] of [['num', 0.8], ['title', 0.9], ['cat', 1]]) if (rendered(p0[k])) job('type', p0[k], prio, { slow: k === 'num' });
    job('line', p0.cap, 1.3, { side: 'bottom' });
    if (p0.arrow) job('pop', p0.arrow, 1.8);
    for (const c of cards.slice(1)) {
      // The copy lands with the first card's text; others then retype each line over it.
      const p = parts(c);
      const copy = job('dupcard', c, 0.7, { p, src: cards[0], sp: p0, deps: [frame0] });
      job('sweep', p.frame, 1.2, { p, deps: [copy] });
      for (const k of ['num', 'title', 'cat']) if (rendered(p[k])) job('retype', p[k], 1.3, { part: k, copy, deps: [copy] });
    }
  }
  // The stack rail: heading and controls, then the logos stamped in from both ends.
  const brand = $('.brand-section', section);
  if (rendered(brand)) {
    job('line', brand, 1.5, { side: 'top' });
    at('type', '.brand-heading > p', 1.6);
    at('type', '.brand-all', 2);
    for (const b of $$('.brand-heading .icon-button', section).filter(rendered)) job('pop', b, 2.4);
    const rail = $('#brand-collection', section);
    const logos = rail ? $$('.brand-logo', rail) : [];
    if (logos.length) { job('stamp', rail, 1.8, { logos, half: 0 }); job('stamp', rail, 1.9, { logos, half: 1 }); }
  }
  const grid = $('.project-grid', section);
  if (heading) job('guide', null, 0.3, { axis: 'x', at: box => box(heading.el).l });
  if (rendered(grid)) job('guide', null, 0.6, { axis: 'y', at: box => box(grid).t });
}

function planAbout(section, job) {
  const at = (kind, sel, prio, o) => { const el = $(sel, section); return rendered(el) ? job(kind, el, prio, o) : null; };
  const step = (k, el, o = {}) => (rendered(el) ? { k, el, ...o } : null);
  // The lead: eyebrow, the heading (then "SCALE." gets stretched to prove it), the bio.
  at('type', '.about-lead .eyebrow', 0.5);
  const heading = at('type', '.about-lead h2', 0.3, { slow: true });
  const word = heading && $('.accent', heading.el);
  if (rendered(word)) job('stretch', word, 0.6, { deps: [heading] });
  at('type', '.about-lead > p', 0.8);
  // The grid's rules, drawn with the pen.
  const grid = $('.studio-grid', section), proc = $('.process-panel', section), quote = $('.quote-panel', section);
  if (rendered(grid)) job('line', grid, 0.7, { side: 'top' });
  if (rendered(proc)) {
    const cs = getComputedStyle(proc);
    if (parseFloat(cs.borderLeftWidth) > 0) job('line', proc, 1.2, { side: 'left' });
    else if (parseFloat(cs.borderTopWidth) > 0) job('line', proc, 1.2, { side: 'top' });
  }
  // Education and skills: one chip is drawn and typed, the rest are option-dragged copies of it.
  at('type', '.education-panel .panel-heading', 0.9);
  at('type', '.education-panel > .eyebrow:not(.skills-heading)', 1.1);
  const row = $('.education-row', section);
  if (rendered(row)) {
    job('seq', row, 1.2, { steps: [step('type', $('strong', row)), step('type', $(':scope > span', row)), step('type', $(':scope > p', row))].filter(Boolean) });
    job('line', row, 1.5, { side: 'bottom' });
  }
  at('type', '.skills-heading', 1.4);
  const chips = $$('.skill-tags > span', section).filter(rendered);
  if (chips.length) {
    const master = job('button', chips[0], 1.5, { caretColor: 'agent' });
    for (const c of chips.slice(1)) job('dupchip', c, 1.8, { src: chips[0], deps: [master] });
  }
  const icons = $$('.software-row img', section).filter(rendered);
  if (icons.length) job('stamp', $('.software-row', section), 2, { logos: icons, half: 'all' });
  // From idea to production: the heading, the track drawn top to bottom, then each step.
  at('type', '.process-panel .panel-heading', 1);
  const track = $('.process-track', section);
  if (rendered(track)) job('line', track, 1.3, { side: 'center', reveal: true });
  $$('.process-row', section).filter(rendered).forEach((r, i) => {
    job('seq', r, 1.4 + i * 0.12, { steps: [step('type', $('.process-num', r), { slow: true }), step('pop', $('.process-node', r), { round: true }), step('type', $('h4', r)), step('type', $('p', r))].filter(Boolean) });
  });
  // The quote card grows under the crosshair, then its words go in and the signature is written.
  if (rendered(quote)) {
    const card = job('frame', quote, 1.1, { bare: true });
    for (const [s, kind, prio] of [['.quote-mark', 'pop', 1.4], ['blockquote', 'type', 1.5], ['.handwriting', 'write', 1.7], ['.quote-bottom', 'type', 1.9]]) {
      const el = $(s, quote);
      if (rendered(el)) job(kind, el, prio, { deps: [card] });
    }
  }
  const lead = $('.about-lead', section);
  if (rendered(lead)) job('guide', null, 0.2, { axis: 'x', at: box => box(lead).l });
  if (rendered(grid)) job('guide', null, 0.6, { axis: 'y', at: box => box(grid).t });
}

function planJourney(section, job) {
  const at = (kind, sel, prio, o) => { const el = $(sel, section); return rendered(el) ? job(kind, el, prio, o) : null; };
  const step = (k, el, o = {}) => (rendered(el) ? { k, el, ...o } : null);
  const heading = at('type', '.section-heading h2', 0.3);
  at('type', '.section-heading .eyebrow', 0.5);
  at('type', '.section-heading .text-link', 1.2);
  // Experience: build the first row (its parts in parallel) and type its open description,
  // then ⌘D drops copies of the row into the slots below and other collaborators retype them.
  const rows = $$('.experience-row', section).filter(rendered);
  if (rows.length) {
    const parts = r => ({ head: $(':scope > button', r), date: $('.experience-date', r), title: $('h3', r), place: $('.experience-place', r), icon: $(':scope > button > svg', r), text: $(':scope > div p', r) });
    const p0 = parts(rows[0]);
    job('line', rows[0], 0.7, { side: 'top' });
    const firsts = {};
    for (const [k, prio] of [['date', 0.75], ['title', 0.8], ['place', 0.9]]) if (rendered(p0[k])) firsts[k] = job('type', p0[k], prio);
    if (rendered(p0.icon)) job('pop', p0.icon, 1);
    if (rendered(p0.text)) job('type', p0.text, 1.1, { deps: firsts.title ? [firsts.title] : [] });
    if (rows.length > 1 && rendered(p0.head)) {
      const copies = rows.slice(1).map(r => ({ row: r, ...parts(r) })).filter(c => rendered(c.head));
      const rep = job('repeat', p0.head, 1, { copies, p0, deps: Object.values(firsts) });
      copies.forEach((c, i) => {
        for (const k of ['date', 'title', 'place']) if (rendered(c[k])) job('retype', c[k], 1.3 + i * 0.05, { part: k, copy: rep, at: i, deps: [rep] });
      });
    }
    const last = rows[rows.length - 1];
    if (parseFloat(getComputedStyle(last).borderBottomWidth) > 0) job('line', last, 1.6, { side: 'bottom' });
  }
  // Milestones: the panel's rule and heading, then each row built by its own collaborator.
  const panel = $('.awards-panel', section);
  if (rendered(panel)) {
    const cs = getComputedStyle(panel);
    if (parseFloat(cs.borderLeftWidth) > 0) job('line', panel, 1, { side: 'left' });
    else if (parseFloat(cs.borderTopWidth) > 0) job('line', panel, 1, { side: 'top' });
    at('type', '.awards-panel > .eyebrow', 1.1);
    at('type', '.awards-panel > h3', 0.9, { slow: true });
    $$('.award-list > button', section).filter(rendered).forEach((b, i) => {
      const [num, label] = $$(':scope > span', b);
      job('seq', b, 1.3 + i * 0.1, { steps: [step('line', b, { side: 'top' }), step('type', num), step('type', label), step('pop', $(':scope > svg', b))].filter(Boolean) });
    });
  }
  const grid = $('.journey-grid', section);
  if (heading) job('guide', null, 0.2, { axis: 'x', at: box => box(heading.el).l });
  if (rendered(grid)) job('guide', null, 0.6, { axis: 'y', at: box => box(grid).t });
}

function planContact(section, job) {
  const at = (kind, sel, prio, o, root = section) => { const el = $(sel, root); return rendered(el) ? job(kind, el, prio, o) : null; };
  // The headline types in small and steps up to full size, like PORTFOLIO did in the hero.
  at('type', '.contact-intro .eyebrow', 0.5);
  const head = at('grow', '.contact-intro h2', 0.3);
  at('type', '.contact-intro > p', 0.9);
  at('type', '.contact-cta', 1.2);
  // Contact rows: build the first, ⌘D a pile of copies, then "Tidy up" spreads them into place.
  const rows = $$('.contact-links > a, .contact-links > div', section).filter(rendered);
  if (rows.length) {
    const parts = r => ({ row: r, icon: $(':scope > svg:first-child', r), text: $(':scope > span', r), arrow: r.tagName === 'A' ? $(':scope > svg:last-child', r) : null });
    const p0 = parts(rows[0]);
    // The first row's rule, icon, text and arrow are built in parallel by different collaborators.
    const firsts = [job('line', rows[0], 0.8, { side: 'bottom' })];
    if (rendered(p0.icon)) firsts.push(job('shape', p0.icon, 0.85));
    if (rendered(p0.text)) firsts.push(job('type', p0.text, 0.8));
    if (rendered(p0.arrow)) firsts.push(job('pop', p0.arrow, 0.95));
    if (rows.length > 1 && rendered(p0.text)) {
      const copies = rows.slice(1).map(parts);
      const tidy = job('tidy', rows[0], 1, { copies, p0, deps: firsts });
      copies.forEach((c, i) => { if (rendered(c.text)) job('retype', c.text, 1.3 + i * 0.05, { part: 'text', copy: tidy, at: i, deps: [tidy] }); });
    }
  }
  // The featured-build card: frame, picture, words, play button, then it is swung to its tilt.
  const card = $('.contact-reel > button', section);
  if (rendered(card)) {
    const screen = $('.reel-screen', card), pic = $('.reel-screen > .media-image', card), parts = [];
    const frame = rendered(screen) ? job('frame', screen, 0.7) : null;
    if (frame && rendered(pic)) parts.push(job('sweep', screen, 1, { p: { frame: screen, pic, badge: null }, deps: [frame] }));
    for (const [s, kind, prio, o] of [['.reel-screen-top', 'type', 1.1], ['.reel-screen-title', 'type', 1.2, { slow: true }], ['.reel-screen > .play-circle', 'shape', 1.4], ['.reel-caption', 'type', 1.6]]) {
      const el = $(s, card);
      if (rendered(el)) parts.push(job(kind, el, prio, { ...o, deps: frame ? [frame] : [] }));
    }
    job('rotate', card, 1.8, { deps: parts });
  }
  // The project brief: its rule and title side by side, then each label types and its field is dragged out
  // (a label holds its field, so the field waits for it), then the button is drawn.
  const brief = $('.project-brief', section);
  if (rendered(brief)) {
    job('line', brief, 1.5, { side: 'top' });
    at('type', ':scope > h3', 1.5, undefined, brief);
    $$('form > label', brief).filter(rendered).forEach((lab, i) => {
      const t = job('type', lab, 1.55 + i * 0.05), field = $('input, select, textarea', lab);
      if (rendered(field)) job('frame', field, 1.6 + i * 0.05, { bare: true, deps: [t] });
    });
    at('button', 'form > button', 1.75, undefined, brief);
    at('type', '.form-note', 1.8, undefined, brief);
  }
  // The footer, last of all.
  const foot = $('.footer');
  if (rendered(foot)) {
    if (parseFloat(getComputedStyle(section).borderBottomWidth) > 0) job('line', section, 2, { side: 'bottom' });
    at('type', ':scope > .wordmark', 2.1, undefined, foot);
    at('type', ':scope > span', 2.2, undefined, foot);
    for (const l of $$(':scope > div > a', foot).filter(rendered)) job('pop', l, 2.4);
  }
  const intro = $('.contact-intro', section), grid = $('.contact-grid', section);
  if (rendered(intro)) job('guide', null, 0.2, { axis: 'x', at: box => box(intro).l });
  if (head && rendered(grid)) job('guide', null, 0.6, { axis: 'y', at: box => box(grid).t });
}

// Hide what a job will build and remember which inline properties it owns,
// so a job the visitor scrolls past can be revealed in one step.
function hideJob(j, put) {
  const hide = el => { if (el) { put(el, 'opacity', '0'); j.own.push([el, 'opacity']); } };
  const line = (el, side) => { put(el, `border-${side}-color`, 'transparent'); j.own.push([el, `border-${side}-color`]); };
  switch (j.kind) {
    case 'line': if (j.reveal) hide(j.el); else line(j.el, j.side); break;
    case 'media': j.img = $('img', j.el); hide(j.img); break;
    case 'duprow': j.parts.forEach(hide); line(j.el, 'bottom'); break;
    case 'frame': hide(j.el); break;
    case 'sweep': hide(j.p.pic); hide(j.p.badge); break;
    case 'dupcard': hide(j.p.frame); hide(j.p.arrow); line(j.p.cap, 'bottom'); break;
    case 'stamp': if (j.half !== 1) j.logos.forEach(el => put(el, 'opacity', '0')); break;
    case 'seq': j.steps.forEach(s => (s.k === 'line' ? line(s.el, s.side) : hide(s.el))); break;
    case 'repeat': j.copies.forEach(c => { line(c.row, 'top'); hide(c.icon); }); break;
    case 'tidy': j.copies.forEach(c => { line(c.row, 'bottom'); hide(c.icon); hide(c.arrow); }); break;
    case 'rotate': {
      // Build the card flat; the tilt is put back by hand at the end. Read the tilt from the
      // stylesheet even if the card was already flattened when its section was armed.
      const prev = j.el.style.transform;
      j.el.style.transform = '';
      const m = /matrix\(([^)]+)\)/.exec(getComputedStyle(j.el).transform);
      j.el.style.transform = prev;
      const k = m ? m[1].split(',').map(Number) : [1, 0];
      j.deg = Math.round((Math.atan2(k[1], k[0]) * 180) / Math.PI * 10) / 10;
      put(j.el, 'transition', 'none'); put(j.el, 'transform', 'none');
      j.own.push([j.el, 'transform'], [j.el, 'transition']);
      break;
    }
    case 'stretch': case 'guide': break;
    default: hide(j.el);
  }
}

/** Decide before the first render whether the intro should play. */
export function planIntro() {
  try {
    const q = new URLSearchParams(location.search);
    if (q.get('intro') === '0') return false;
    if (!q.has('intro') && (sessionStorage.getItem(KEY) || (location.hash && location.hash !== '#home'))) return false;
    if (matchMedia('(prefers-reduced-motion: reduce)').matches) return false;
    if (localStorage.getItem('portfolio-motion') === 'off') return false;
    if (!(window.CSS && CSS.supports('scale', '1') && CSS.supports('clip-path', 'polygon(0 0,1px 0,0 1px)'))) return false;
    if (innerWidth < 300 || innerHeight < 420) return false;
    if ('scrollRestoration' in history) history.scrollRestoration = 'manual';
    if (scrollY) scrollTo(0, 0);
    return true;
  } catch { return false; }
}

/** Decide before the first render whether the crew builds a section when it scrolls into view. */
function planBuild(key) {
  try {
    const q = new URLSearchParams(location.search);
    if (q.get('intro') === '0') return false;
    if (!q.has('intro') && (sessionStorage.getItem(key) || (location.hash && location.hash !== '#home'))) return false;
    if (matchMedia('(prefers-reduced-motion: reduce)').matches) return false;
    if (localStorage.getItem('portfolio-motion') === 'off') return false;
    if (!(window.CSS && CSS.supports('scale', '1') && CSS.supports('clip-path', 'polygon(0 0,1px 0,0 1px)'))) return false;
    return innerWidth >= 300 && innerHeight >= 420;
  } catch { return false; }
}
export const planWorkBuild = () => planBuild(WORK_KEY);
export const planAboutBuild = () => planBuild(ABOUT_KEY);
export const planJourneyBuild = () => planBuild(JOURNEY_KEY);
export const planContactBuild = () => planBuild(CONTACT_KEY);

/** Run the intro over the rendered page. Call from a layout effect so the hero is hidden before the first paint. */
export function startIntro() { return createSwarm('intro', { keep: keeper() }); }

/**
 * Hide a section now (call from a layout effect) and let the crew build it the
 * first time it scrolls into view. Only one build runs at a time, so a section
 * waits for the intro or the section before it to finish.
 */
export const armWork = onDone => armSection('portfolio', planWork, WORK_KEY, 'work', onDone);
export const armAbout = onDone => armSection('about', planAbout, ABOUT_KEY, 'about', onDone);
export const armJourney = onDone => armSection('experience', planJourney, JOURNEY_KEY, 'journey', onDone);
export const armContact = onDone => armSection('contact', planContact, CONTACT_KEY, 'contact', onDone);
// Sections armed and waiting for the crew, in page order. A crew that finishes a section
// while the visitor is already in the next one hands itself straight over instead of leaving.
const waitingSections = [];
const nextReady = () => waitingSections.find(s => s.ready());
function armSection(id, plan, key, name, onDone) {
  const html = document.documentElement;
  const section = document.getElementById(id);
  if (!section) return null;
  const keep = keeper();
  const { jobs, job } = makeJobs();
  plan(section, job);
  jobs.forEach(j => hideJob(j, keep.put));
  const rail = $('#brand-collection', section);
  if (rail) rail.dataset.hold = '1';
  // Fetch the pictures and logos early (after the page has loaded) so the sweeps never wait.
  const warm = () => setTimeout(() => $$('img[loading="lazy"]', section).forEach(im => { im.loading = 'eager'; }), 900);
  if (document.readyState === 'complete') warm(); else addEventListener('load', warm, { once: true });
  let run = null, stopped = false;
  const events = ['scroll', 'resize', 'swarm:intro-done', 'swarm:build-done'];
  const off = () => events.forEach(e => removeEventListener(e, check));
  const release = () => { keep.restoreAll(); if (rail) delete rail.dataset.hold; };
  const entry = {
    // In view: past the top of the page, with the section's top in the lower quarter of the screen or above.
    ready() {
      if (run || stopped || window.scrollY < 24) return false;
      const r = section.getBoundingClientRect();
      return r.top <= innerHeight * 0.74 && r.bottom >= 120;
    },
    start(handoff) {
      off();
      const at = waitingSections.indexOf(entry);
      if (at >= 0) waitingSections.splice(at, 1);
      try { sessionStorage.setItem(key, '1'); } catch {}
      run = createSwarm('section', { keep, root: section, rail, onDone, plan, name, handoff });
      if (!run) { release(); if (onDone) onDone(); }
    },
  };
  waitingSections.push(entry);
  function check() {
    // Only one crew at a time: wait for the intro or another section's build to finish.
    if (html.dataset.intro || html.dataset.build || !entry.ready()) return;
    entry.start(null);
  }
  events.forEach(e => addEventListener(e, check, e === 'scroll' ? { passive: true } : undefined));
  requestAnimationFrame(check);
  return {
    stop() {
      stopped = true; off();
      const at = waitingSections.indexOf(entry);
      if (at >= 0) waitingSections.splice(at, 1);
      if (run) run.stop(); else release();
    },
  };
}

function createSwarm(mode, opts) {
  const born = performance.now();
  const intro = mode === 'intro';
  const html = document.documentElement;
  const header = $('.site-header');
  const hero = document.getElementById('home');
  const root = intro ? hero : opts.root;
  if (!header || !root) return null;
  if (intro) { try { sessionStorage.setItem(KEY, '1'); } catch {} }

  const vw = innerWidth, vh = innerHeight, small = vw < 601;
  const DPR = Math.min(window.devicePixelRatio || 1, 2);
  const RULER = small ? 16 : 20;
  const BADGE = `600 ${small ? 9 : 10}px Manrope, system-ui, sans-serif`;
  const TINY = `600 ${small ? 7 : 8}px Manrope, system-ui, sans-serif`;
  const { saved, put, back } = opts.keep;
  const hide = el => put(el, 'opacity', '0');
  const show = el => back(el, 'opacity');
  // Everything is placed in page coordinates. The intro runs at the top of a
  // locked page; the Work build follows the page while the visitor scrolls.
  const sy = () => (intro ? 0 : window.scrollY);
  const view = { t: 0 };
  const box = el => { const r = el.getBoundingClientRect(), s = sy(); return { l: r.left, t: r.top + s, r: r.right, b: r.bottom + s, w: r.width, h: r.height }; };
  const visible = el => {
    if (!el) return false;
    const r = el.getBoundingClientRect();
    return r.width >= 1 && r.height >= 1 && r.bottom > 0 && r.top < vh && r.right > 0 && r.left < vw && getComputedStyle(el).visibility !== 'hidden';
  };

  // ------------------------------------------------------------------ jobs
  const { jobs, job } = makeJobs();
  const media = intro ? planHero(hero, header, job, visible, vh, small) : (opts.plan(root, job), null);
  jobs.forEach(j => hideJob(j, put));
  const rest = intro ? [...$$('main > section'), $('.footer')].filter(el => el && el !== hero) : [];
  if (intro) {
    rest.forEach(hide);
    put(html, 'overflow', 'hidden');
    put(html, 'scrollbar-gutter', 'stable');
    html.dataset.intro = 'warmup';
  } else html.dataset.build = opts.name;

  // --------------------------------------------------------------- overlay
  const style = document.createElement('style');
  style.textContent = STYLE;
  document.head.append(style);
  const layer = document.createElement('div');
  layer.className = intro ? 'swarm-layer' : 'swarm-layer swarm-pass';
  layer.setAttribute('aria-hidden', 'true');
  const cv = document.createElement('canvas');
  cv.width = Math.round(vw * DPR); cv.height = Math.round(vh * DPR);
  cv.style.width = `${vw}px`; cv.style.height = `${vh}px`;
  layer.append(cv);
  const g = cv.getContext('2d');

  const N = small ? 10 : vw < 1100 ? 12 : 14;
  const cx = vw / 2, cy = vh * (small ? 0.44 : 0.47);
  const orbitR = clamp(Math.min(vw, vh) * 0.21, 92, 210);
  const s0 = sy();
  const agents = CREW.slice(0, N).map(([name, color], i) => {
    const a = {
      i, name, color, ink: lum(color) > 0.5 ? '#0d0d0d' : '#ffffff',
      x: 0, y: 0, vx: 0, vy: 0, rot: 0, shape: 'arrow', mode: 'idle', alpha: 0, label: 0, scale: 1, down: false, trail: [], tA: 1,
      r: orbitR * rnd(0.5, 1.05), w: rnd(1.7, 2.5), ph: rnd(0, 6.3),
    };
    if (intro) {
      // Each one joins somewhere around the logo, spread over the screen.
      const ang = (i / N) * Math.PI * 2 + rnd(-0.3, 0.3), k = rnd(0.42, 0.92);
      a.x = clamp(cx + Math.cos(ang) * vw * 0.46 * k, 30, vw - 90); a.y = clamp(cy + Math.sin(ang) * vh * 0.44 * k, 40, vh - 80);
    } else if (opts.handoff) {
      // Handed over by the crew that just finished the section above: carry on from where they are.
      const h = opts.handoff.agents[i];
      if (h) Object.assign(a, h, { trail: h.trail.slice(), mode: 'script' }); else { a.x = vw / 2; a.y = s0; }
    } else {
      // Placed when each one starts work: out of the visitor's pointer (see worker).
      a.x = vw / 2; a.y = s0; a.scale = 0.5;
    }
    return a;
  });

  const pill = document.createElement('div');
  pill.className = opts.handoff ? 'swarm-pill on' : 'swarm-pill';
  pill.innerHTML = `<span class="swarm-faces">${agents.slice(0, 5).map(a => `<i style="background:${a.color}"></i>`).join('')}</span><span><b>${intro ? 0 : N}</b> agents</span><span class="swarm-sep"></span><span class="swarm-count">${intro ? 'Connecting' : 'Building'}</span><span class="swarm-sep"></span><button class="swarm-skip" type="button" aria-label="${intro ? 'Skip intro' : 'Skip build'}">Skip<kbd>Esc</kbd></button>`;
  const count = pill.querySelector('.swarm-count'), joinedEl = pill.querySelector('b');
  document.body.append(layer, pill);

  // --------------------------------------------------------------- sprites
  const SPR = 30;
  function sprite(color, kind) {
    const s = SHAPES[kind], c = document.createElement('canvas');
    c.width = c.height = Math.ceil(SPR * DPR);
    const x = c.getContext('2d');
    x.scale(DPR, DPR); x.translate(3, 3);
    const p = new Path2D(s.d), detail = lum(color) > 0.6 ? '#151515' : '#ffffff';
    x.lineJoin = 'round'; x.lineCap = 'round';
    x.shadowColor = 'rgba(0,0,0,.6)'; x.shadowBlur = 4; x.shadowOffsetY = 1;
    if (s.fill) {
      x.fillStyle = color; x.strokeStyle = '#fff'; x.lineWidth = 1.5;
      x.fill(p); x.stroke(p); x.shadowColor = 'transparent'; x.fill(p); x.stroke(p);
    } else {
      x.strokeStyle = '#fff'; x.lineWidth = 3.6; x.stroke(p);
      x.shadowColor = 'transparent'; x.strokeStyle = color; x.lineWidth = 1.6; x.stroke(p);
    }
    if (s.nib) {
      x.strokeStyle = detail; x.lineWidth = 1.1;
      x.beginPath(); x.moveTo(3.6, 3.6); x.lineTo(8.9, 8.9); x.stroke();
      x.beginPath(); x.arc(10.2, 10.2, 1.5, 0, Math.PI * 2); x.stroke();
    }
    if (s.plus) {
      x.beginPath(); x.arc(18.6, 18.6, 4.4, 0, Math.PI * 2); x.fillStyle = color; x.fill();
      x.strokeStyle = '#fff'; x.lineWidth = 1.4; x.stroke();
      x.strokeStyle = detail; x.beginPath(); x.moveTo(18.6, 16.5); x.lineTo(18.6, 20.7); x.moveTo(16.5, 18.6); x.lineTo(20.7, 18.6); x.stroke();
    }
    return { c, hx: s.hx + 3, hy: s.hy + 3 };
  }
  // Cursor sprites are drawn on first use, so start-up stays cheap.
  const SP = agents.map(() => ({}));
  const spriteOf = (a, k) => SP[a.i][k] || (SP[a.i][k] = sprite(a.color, k));
  const LB = [];
  function labelSprite(a) {
    const f = `700 ${small ? 9.5 : 10.5}px Manrope, system-ui, sans-serif`;
    g.font = f; curFont = f;
    const w = Math.ceil(g.measureText(a.name).width) + 12, h = small ? 17 : 19;
    const c = document.createElement('canvas');
    c.width = Math.ceil((w + 6) * DPR); c.height = Math.ceil((h + 6) * DPR);
    const x = c.getContext('2d');
    x.scale(DPR, DPR);
    x.shadowColor = 'rgba(0,0,0,.45)'; x.shadowBlur = 3; x.shadowOffsetY = 1;
    x.fillStyle = a.color; rr(x, 2, 2, w, h, 5); x.fill();
    x.shadowColor = 'transparent';
    x.fillStyle = a.ink; x.font = f; x.textBaseline = 'middle';
    x.fillText(a.name, 8, 2 + h / 2 + 0.5);
    return { c, w: w + 6, h: h + 6 };
  }
  function makeTile(S) {
    const pad = 44, W = S + pad * 2, c = document.createElement('canvas');
    c.width = c.height = Math.ceil(W * DPR);
    const x = c.getContext('2d');
    x.scale(DPR, DPR);
    x.shadowColor = 'rgba(227,43,60,.62)'; x.shadowBlur = 46;
    x.fillStyle = '#121214'; rr(x, pad, pad, S, S, S * 0.26); x.fill();
    x.shadowColor = 'transparent'; x.strokeStyle = '#2d2d33'; x.lineWidth = 1;
    rr(x, pad + 0.5, pad + 0.5, S - 1, S - 1, S * 0.26); x.stroke();
    return { c, w: W };
  }
  const tile = { a: 0, s: 0.8, draw: 0, spr: intro ? makeTile(small ? 52 : 62) : null, path: new Path2D(S_PATH) };
  const rul = { t: opts.handoff ? opts.handoff.rul : 0, top: null };
  function buildRulers() {
    const R = RULER, c = document.createElement('canvas');
    c.width = Math.ceil(vw * DPR); c.height = Math.ceil(R * DPR);
    const tx = c.getContext('2d');
    tx.scale(DPR, DPR);
    tx.fillStyle = 'rgba(14,14,16,.97)'; tx.fillRect(0, 0, vw, R);
    tx.fillStyle = '#2a2a2f'; tx.fillRect(0, R - 1, vw, 1);
    tx.fillStyle = '#3d3d44';
    for (let x = 0; x <= vw; x += 10) { const h = x % 100 === 0 ? R * 0.55 : x % 50 === 0 ? 5 : 3; tx.fillRect(x, R - 1 - h, 1, h); }
    tx.font = TINY; tx.fillStyle = '#6e6e76'; tx.textBaseline = 'top';
    for (let x = 100; x < vw; x += 100) tx.fillText(String(x), x + 3, small ? 2 : 3);
    rul.top = c;
  }

  // ----------------------------------------------------------- measurement
  // Character boxes in the element's own coordinates, grouped into lines,
  // plus a map from those coordinates to the page (rotation and scale aware).
  const range = document.createRange();
  function measure(el) {
    const cs = getComputedStyle(el), tf = cs.transform, to = cs.transformOrigin;
    const turned = !!tf && tf !== 'none';
    if (turned) put(el, 'transform', 'none');
    const B = el.getBoundingClientRect(), s = sy();
    const chars = [];
    const walk = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
    for (let n = walk.nextNode(); n; n = walk.nextNode()) {
      const ps = getComputedStyle(n.parentElement);
      if (ps.visibility === 'hidden' || parseFloat(ps.fontSize) < 1) continue;
      const font = `${ps.fontStyle} ${ps.fontWeight} ${ps.fontSize} ${ps.fontFamily}`, up = ps.textTransform === 'uppercase', str = n.data;
      for (let i = 0; i < str.length; i++) {
        range.setStart(n, i); range.setEnd(n, i + 1);
        const rs = range.getClientRects(), r = rs[rs.length - 1];
        if (!r || r.width < 0.01) continue;
        chars.push({ ch: up ? str[i].toUpperCase() : str[i], l: r.left - B.left, t: r.top - B.top, r: r.right - B.left, b: r.bottom - B.top, font, color: ps.color, space: /\s/.test(str[i]) });
      }
    }
    if (turned) back(el, 'transform');
    const lines = [];
    chars.forEach((c, i) => {
      const L = lines[lines.length - 1];
      if (L && c.l >= chars[L.end].l - 1 && Math.min(L.b, c.b) - Math.max(L.t, c.t) > Math.min(L.b - L.t, c.b - c.t) * 0.3) {
        L.t = Math.min(L.t, c.t); L.b = Math.max(L.b, c.b); L.l = Math.min(L.l, c.l); L.r = Math.max(L.r, c.r); L.end = i;
      } else lines.push({ t: c.t, b: c.b, l: c.l, r: c.r, end: i });
      c.line = lines.length - 1;
    });
    const tb = lines.length
      ? { l: Math.min(...lines.map(L => L.l)), t: lines[0].t, r: Math.max(...lines.map(L => L.r)), b: lines[lines.length - 1].b }
      : { l: 0, t: 0, r: B.width, b: B.height };
    let M = [1, 0, 0, 1, 0, 0];
    if (turned) { const mm = /matrix\(([^)]+)\)/.exec(tf); if (mm) M = mm[1].split(',').map(Number); }
    const o = to.split(' ').map(parseFloat);
    const m = { el, B: { l: B.left, t: B.top + s, w: B.width, h: B.height }, chars, lines, n: chars.length, tb, ox: o[0] || 0, oy: o[1] || 0, s: 1 };
    m.map = (lx, ly) => {
      const px = lx - m.ox, py = ly - m.oy, k = m.s;
      return [m.B.l + m.ox + k * (M[0] * px + M[2] * py + M[4]), m.B.t + m.oy + k * (M[1] * px + M[3] * py + M[5])];
    };
    return m;
  }
  const boxOf = m => { const [l, t] = m.map(m.tb.l, m.tb.t), [r, b] = m.map(m.tb.r, m.tb.b); return { l, t, r, b, w: r - l, h: b - t }; };
  // Everything above the current line, plus the current line up to the caret.
  function clipFor(m, k) {
    if (k >= m.n) return null;
    if (k <= 0) return 'polygon(0 0,0 0,0 0)';
    const c = m.chars[k - 1], i = c.line, L = m.lines[i], P = 64;
    const top = i ? (m.lines[i - 1].b + L.t) / 2 : -P;
    const bot = i < m.lines.length - 1 ? (L.b + m.lines[i + 1].t) / 2 : m.B.h + P;
    const W = m.B.w + P, x = (c.r + 0.6).toFixed(1);
    return `polygon(${-P}px ${-P}px,${W}px ${-P}px,${W}px ${top}px,${x}px ${top}px,${x}px ${bot}px,${-P}px ${bot}px)`;
  }
  function survey() {
    const s = sy(), H = intro ? box(hero) : null;
    for (const j of jobs) {
      const el = j.el;
      // Section builds measure letters just before a layer is built, so the start stays light.
      // The intro measures everything at its burst, while the page is still locked.
      const measured = fn => { if (intro) fn(); else j.prep = fn; };
      if (j.kind === 'type' || j.kind === 'write' || j.kind === 'grow' || j.kind === 'retype') {
        if (intro) { j.m = measure(el); [j.ax, j.ay] = j.m.map(j.m.tb.l, j.m.tb.t); }
        else { const b = box(el); j.ax = b.l; j.ay = b.t; j.prep = () => { j.m = measure(el); }; }
      } else if (j.kind === 'dup') {
        if (intro) { j.m = measure(el); j.sm = measure(j.src); [j.ax, j.ay] = j.sm.map(j.sm.tb.l, j.sm.tb.t); }
        else { const b = box(j.src); j.ax = b.l; j.ay = b.t; j.prep = () => { j.m = measure(el); j.sm = measure(j.src); }; }
      } else if (j.kind === 'button') { j.B = box(el); j.ax = j.B.l; j.ay = j.B.t; measured(() => { j.m = measure(el); }); }
      else if (j.kind === 'shape' || j.kind === 'pop' || j.kind === 'frame') { j.B = box(el); j.ax = j.B.l; j.ay = j.B.t; }
      else if (j.kind === 'media') {
        const b = box(el), l = Math.max(b.l, H.l, 0), t = Math.max(b.t, H.t, s), r = Math.min(b.r, H.r, vw), bt = Math.min(b.b, H.b, s + vh);
        j.C = { l, t, r, b: bt, w: r - l, h: bt - t }; j.ir = j.img ? box(j.img) : null; j.ax = l; j.ay = t;
      } else if (j.kind === 'line') {
        const b = box(el);
        if (j.side === 'left' || j.side === 'center') {
          // Vertical rules (a column border, or a track drawn down its middle).
          const w = j.side === 'left' ? parseFloat(getComputedStyle(el).borderLeftWidth) || 1 : 0;
          const x = j.side === 'left' ? b.l + w / 2 : b.l + b.w / 2;
          j.seg = { v: true, x, y0: b.t, y1: b.b }; j.ax = x; j.ay = b.t;
        } else {
          const w = parseFloat(getComputedStyle(el).getPropertyValue(`border-${j.side}-width`)) || 1;
          const y = j.side === 'top' ? b.t + w / 2 : b.b - w / 2;
          j.seg = { x0: Math.max(b.l, 0), x1: Math.min(b.r, vw), y }; j.ax = j.seg.x0; j.ay = y;
        }
      } else if (j.kind === 'seq') {
        for (const st of j.steps) {
          st.B = box(st.el);
          if (st.k === 'line') {
            const w = parseFloat(getComputedStyle(st.el).getPropertyValue(`border-${st.side}-width`)) || 1;
            st.seg = { x0: Math.max(st.B.l, 0), x1: Math.min(st.B.r, vw), y: st.side === 'top' ? st.B.t + w / 2 : st.B.b - w / 2 };
          }
        }
        const f = j.steps[0];
        j.ax = f ? f.B.l : 0; j.ay = f ? f.B.t : s;
        measured(() => { for (const st of j.steps) if (st.k === 'type') st.m = measure(st.el); });
      } else if (j.kind === 'tidy') {
        j.S = box(el);
        for (const c of j.copies) c.B = box(c.row);
        j.ax = j.S.l + j.S.w / 2; j.ay = j.S.t + j.S.h / 2;
        measured(() => { j.sm = measure(j.p0.text); j.sms = [j.sm]; j.tx = boxOf(j.sm); });
      } else if (j.kind === 'rotate') {
        j.B = box(el); j.ax = j.B.r; j.ay = j.B.b;
      } else if (j.kind === 'repeat') {
        j.S = box(el);
        for (const c of j.copies) c.B = box(c.head);
        j.ax = j.S.l + j.S.w / 2; j.ay = j.S.t + j.S.h / 2;
        measured(() => {
          j.smap = {};
          for (const k of ['date', 'title', 'place']) if (rendered(j.p0[k])) j.smap[k] = measure(j.p0[k]);
          j.sms = Object.values(j.smap);
        });
      } else if (j.kind === 'stretch') {
        j.B = box(el); j.ax = j.B.r; j.ay = j.B.t;
      } else if (j.kind === 'dupchip') {
        j.SB = box(j.src); j.TB = box(el);
        j.ax = j.SB.l + j.SB.w / 2; j.ay = j.SB.t + j.SB.h / 2;
        measured(() => { j.sm = measure(j.src); j.m = measure(el); });
      } else if (j.kind === 'duprow') {
        j.B = box(el); j.SB = box(j.src); j.ax = j.SB.l; j.ay = j.SB.t;
        measured(() => { j.mm = new Map(j.parts.filter(Boolean).map(p => [p, measure(p)])); j.sms = j.srcParts.filter(Boolean).map(p => measure(p)); });
      } else if (j.kind === 'sweep') {
        j.B = box(el); j.pr = j.p.pic ? box(j.p.pic) : null; j.ax = j.B.l + j.B.w / 2; j.ay = j.B.t + j.B.h / 2;
      } else if (j.kind === 'dupcard') {
        const sp = j.sp, p = j.p;
        j.SB = box(sp.frame); j.TB = box(p.frame); j.SC = box(j.src); j.TC = box(el); j.Scap = box(sp.cap); j.Tcap = box(p.cap);
        j.ax = j.SB.l + j.SB.w / 2; j.ay = j.SB.t + j.SB.h / 2;
        measured(() => {
          j.smap = {};
          for (const k of ['num', 'title', 'cat']) if (rendered(sp[k])) j.smap[k] = measure(sp[k]);
          j.sms = Object.values(j.smap);
        });
      } else if (j.kind === 'stamp') {
        // Logos inside the rail's window are stamped in from both ends; the rest just appear.
        const R = box(el), seen = [], hidden = [];
        for (const lg of j.logos) { const b = box(lg); (b.r > R.l + 4 && b.l < R.r - 4 ? seen : hidden).push({ el: lg, x: b.l + b.w / 2, y: b.t + b.h / 2 }); }
        const mid = Math.ceil(seen.length / 2);
        j.list = j.half === 'all' ? seen : j.half === 0 ? seen.slice(0, mid) : seen.slice(mid).reverse();
        j.extra = j.half === 1 ? [] : hidden.map(o => o.el);
        j.own = [...j.list.map(o => [o.el, 'opacity']), ...j.extra.map(e => [e, 'opacity'])];
        const f = j.list[0] || { x: R.l, y: R.t };
        j.ax = f.x; j.ay = f.y;
      } else if (j.kind === 'guide') {
        j.pos = j.at(box);
        j.ax = j.axis === 'x' ? 0 : j.pos; j.ay = j.axis === 'x' ? s + vh / 2 : j.pos;
      }
    }
  }

  // ------------------------------------------------------------- runtime
  const tasks = [];
  const spawn = it => { it.next(); tasks.push(it); return it; };
  const ui = new Set();
  const addUI = o => { ui.add(o); return o; };
  function* fadeOut(o, ms) { const a0 = o.alpha ?? 1; yield* tween(ms, p => (o.alpha = a0 * (1 - p)), linear); ui.delete(o); }
  const later = (ms, fn) => spawn((function* () { yield* wait(ms); yield* fn(); })());
  const ptr = pointer;
  const onScreen = p => !!p && p.x > 8 && p.y > 8 && p.x < vw - 8 && p.y < vh - 8;
  // Where the crew flocks at the end: the pointer by default (in page coordinates).
  let goal = () => ({ x: ptr.x, y: ptr.y + sy() });
  let squeeze = 1, flushAll = false;
  let vt = 0, frameDt = 16, speed = 1, done = 0, ready = false, over = false, raf = 0, last = 0;
  let shown = -1;
  const tally = () => { if (shown !== done) { shown = done; count.textContent = `${done}/${jobs.length} layers`; } };
  // ?intro=debug (or ?swarm=debug, which leaves the session rules alone) exposes a job
  // timeline (agent, kind, element, start, end) for tuning.
  const log = /[?&](intro|swarm)=debug/.test(location.search) ? (window.__introLog = []) : null;
  // In debug mode the clock can be frozen at a given moment for frame-exact screenshots.
  const dbg = log ? (window.__introCtl = { pause: window.__introPauseAt ?? null, vt: 0, mode: intro ? 'intro' : opts.name }) : null;
  // The Work build only takes on layers the visitor can see: below the sticky
  // header and above the bottom of the screen. Anything above that line was
  // scrolled past and is revealed in one step instead.
  const headerH = header.getBoundingClientRect().height;
  const above = () => sy() + headerH - 16;
  const reach = j => intro || j.ay == null || (j.ay > above() && j.ay < sy() + vh - 24);
  // Where the Work crew bursts out: the visitor's mouse pointer, else their last
  // touch, else just under the header (then they fan downwards).
  const origin = () => {
    const s = sy();
    if (ptr.mouse && onScreen(ptr)) return { x: ptr.x, y: ptr.y + s };
    if (onScreen(ptr.touch) && ptr.touch.y > headerH) return { x: ptr.touch.x, y: ptr.touch.y + s };
    return { x: vw / 2 + rnd(-24, 24), y: s + headerH + 6, top: true };
  };
  let burst = false;

  function* moveTo(a, x, y, o = {}) {
    const x0 = a.x, y0 = a.y, dx = x - x0, dy = y - y0, d = Math.hypot(dx, dy);
    if (d < 0.5 && !o.carry) { a.x = x; a.y = y; return; }
    const T = o.dur ?? clamp(120 + 62 * Math.log2(1 + d / 24), 140, 480);
    let p1x, p1y, p2x, p2y;
    if (o.carry) {
      // Leave orbit along the current heading, then settle onto the target.
      const k = T / 3000;
      p1x = x0 + a.vx * k; p1y = y0 + a.vy * k; p2x = x - dx * 0.1; p2y = y - dy * 0.1;
    } else {
      const bend = (o.bend ?? rnd(-0.16, 0.16)) * d, nx = d ? -dy / d : 0, ny = d ? dx / d : 0;
      p1x = x0 + dx * 0.25 + nx * bend; p1y = y0 + dy * 0.25 + ny * bend;
      p2x = x0 + dx * 0.8 + nx * bend * 0.4; p2y = y0 + dy * 0.8 + ny * bend * 0.4;
    }
    const r0 = wrap(a.rot);
    yield* tween(T, p => {
      const q = 1 - p, A = q * q * q, B = 3 * q * q * p, C = 3 * q * p * p, D = p * p * p;
      a.x = A * x0 + B * p1x + C * p2x + D * x; a.y = A * y0 + B * p1y + C * p2y + D * y;
      if (r0) a.rot = r0 * (1 - out3(Math.min(1, p * 1.5)));
    }, o.carry ? linear : human);
    a.rot = 0; a.vx = 0; a.vy = 0;
  }
  function* press(a) { a.down = true; addUI({ type: 'ring', x: a.x, y: a.y, r0: 2, r1: 13, t: 0, dur: 280, color: a.color, a0: 0.75, w0: 1.5 }); yield* wait(60); }
  const release = a => { a.down = false; };
  function* dragBox(a, B, o = {}) {
    const W = B.r - B.l, H = B.b - B.t;
    yield* press(a);
    const sel = addUI({ type: 'sel', x: B.l, y: B.t, w: 0, h: 0, color: a.color, ink: a.ink, measure: !!o.measure, ellipse: !!o.ellipse });
    yield* tween(o.dur ?? clamp(110 + Math.hypot(W, H) * 0.3, 150, 420), p => {
      a.x = B.l + W * p; a.y = B.t + H * p; sel.w = W * p; sel.h = H * p;
      sel.badge = `${Math.round(sel.w)} × ${Math.round(sel.h)}`;
      if (o.each) o.each(sel.w, sel.h);
    }, human);
    release(a);
    sel.badge = ''; sel.measure = false;
    return sel;
  }
  function* typeChars(m, per, onK) {
    const due = []; let acc = 0;
    for (const c of m.chars) { acc += per * (c.space ? 1.8 : rnd(0.6, 1.4)); due.push(acc); }
    let t = 0, k = 0;
    while (k < m.n) {
      t += yield;
      let k2 = k;
      while (k2 < m.n && due[k2] <= t) k2++;
      if (k2 !== k) { k = k2; onK(k); }
    }
  }
  function* typeIn(a, el, m, o = {}) {
    if (!m || !m.n) { yield* popIn(a, el, o); return; }
    const [x0, y0] = m.map(m.tb.l, m.tb.t - 1), [x1, y1] = m.map(m.tb.r + 2, m.tb.b + 1);
    // Short one-line labels get a click and an auto-width box that grows with the text.
    const auto = !o.retype && m.lines.length === 1 && m.n <= 34;
    let sel = null;
    if (o.retype) {
      const tx = x0 + 1, ty = (y0 + y1) / 2;
      yield* moveTo(a, tx, ty, Math.hypot(tx - a.x, ty - a.y) > 120 || o.carry ? { carry: o.carry } : { dur: 160 });
      a.shape = 'ibeam'; yield* press(a); release(a);
      // Select the copied text, then type the real text over it.
      if (o.stale) { o.stale.hl = true; yield* wait(90); ui.delete(o.stale); }
    } else if (auto) {
      yield* moveTo(a, x0 + 1, (y0 + y1) / 2, { carry: o.carry });
      a.shape = 'ibeam'; yield* press(a); release(a);
      sel = addUI({ type: 'sel', x: x0, y: y0, w: 2, h: y1 - y0, color: a.color, ink: a.ink });
    } else {
      yield* moveTo(a, x0, y0, { carry: o.carry });
      a.shape = 'ibeam';
      sel = yield* dragBox(a, { l: x0, t: y0, r: x1, b: y1 });
    }
    show(el); put(el, 'clip-path', clipFor(m, 0));
    const caret = addUI({ type: 'caret', m, k: 0, color: o.caret || a.color, typing: true });
    yield* typeChars(m, o.per ?? clamp(420 / m.n, 4.5, 26), k => {
      caret.k = k;
      const c = clipFor(m, k);
      if (c) put(el, 'clip-path', c); else back(el, 'clip-path');
      if (auto) sel.w = Math.max(2, m.map(m.chars[k - 1].r + 2, 0)[0] - x0);
    });
    back(el, 'clip-path');
    caret.typing = false; a.shape = 'arrow';
    if (sel) sel.handles = true;
    later(o.hold ?? 160, function* () { ui.delete(caret); if (sel) yield* fadeOut(sel, 220); });
  }
  function* popScale(el, from = 0.4) {
    put(el, 'scale', String(from));
    yield* tween(260, p => put(el, 'scale', lerp(from, 1, p).toFixed(3)), outBack);
    back(el, 'scale');
  }
  function* popIn(a, el, o = {}) {
    const B = o.B || box(el);
    yield* moveTo(a, B.l, B.t, { carry: o.carry });
    a.shape = 'cross';
    const sel = yield* dragBox(a, B, { ellipse: o.round });
    sel.handles = !o.round; a.shape = 'arrow';
    show(el);
    yield* popScale(el);
    later(140, function* () { yield* fadeOut(sel, 220); });
  }
  // The pen tool: click at one end, drag the rule out, click at the other end.
  // Vertical rules stop at the bottom of the screen; the real border appears in full.
  function* penLine(a, sg, carry) {
    let ln;
    if (sg.v) {
      const x = sg.x, y0 = Math.max(sg.y0, sy() + headerH), y1 = Math.max(y0 + 20, Math.min(sg.y1, sy() + vh - 16));
      yield* moveTo(a, x, y0, { carry });
      a.shape = 'pen'; yield* press(a);
      ln = addUI({ type: 'pen', x0: x, y0, x1: x, y1: y0, color: a.color });
      release(a);
      yield* tween(clamp((y1 - y0) * 0.3, 220, 520), p => { a.y = lerp(y0, y1, p); ln.y1 = a.y; }, inOut3);
    } else {
      const { x0, x1, y } = sg, sx = clamp(x0, 3, vw - 3), ex = clamp(x1, 3, vw - 3);
      yield* moveTo(a, sx, y, { carry });
      a.shape = 'pen'; yield* press(a);
      ln = addUI({ type: 'pen', x0: sx, y0: y, x1: sx, y1: y, color: a.color });
      release(a);
      yield* tween(clamp(Math.abs(ex - sx) * 0.3, 220, 460), p => { a.x = lerp(sx, ex, p); ln.x1 = a.x; }, inOut3);
    }
    yield* press(a); ln.closed = true; release(a);
    a.shape = 'arrow';
    later(120, function* () { yield* fadeOut(ln, 320); });
  }
  // An empty frame shows the image placeholder until its picture is dropped in.
  const placeholder = el => {
    put(el, 'background-image', ICON); put(el, 'background-repeat', 'no-repeat');
    put(el, 'background-position', 'center'); put(el, 'background-size', small ? '28px' : '38px');
  };
  const unplaceholder = el => ['background-image', 'background-repeat', 'background-position', 'background-size'].forEach(p => back(el, p));
  function* duplicate(a, ms, S, T, carry, targets, onDrop) {
    // Option-drag: grab the source, pull a copy into place, then retype it.
    yield* moveTo(a, S.l + S.w * 0.5, S.t + S.h * 0.5, { carry });
    a.shape = 'dup';
    const sel = addUI({ type: 'sel', x: S.l, y: S.t, w: S.w, h: S.h, color: a.color, ink: a.ink, handles: true });
    yield* press(a);
    const ghost = addUI({ type: 'ghost', ms, dx: 0, dy: 0, color: a.color });
    const gap = addUI({ type: 'gap', x0: 0, y0: 0, x1: 0, y1: 0 });
    const ax = a.x, ay = a.y, DX = T.l - S.l, DY = T.t - S.t, vert = Math.abs(DY) > Math.abs(DX);
    yield* tween(clamp(Math.hypot(DX, DY), 180, 360), p => {
      const dx = DX * p, dy = DY * p;
      ghost.dx = dx; ghost.dy = dy; a.x = ax + dx; a.y = ay + dy; sel.x = S.l + dx; sel.y = S.t + dy;
      if (vert) Object.assign(gap, { x0: S.l - 8, y0: S.t, x1: S.l - 8, y1: S.t + dy });
      else Object.assign(gap, { x0: S.l, y0: S.t - 8, x1: S.l + dx, y1: S.t - 8 });
    }, human);
    release(a); ui.delete(gap);
    Object.assign(sel, { x: T.l, y: T.t, w: T.w, h: T.h });
    if (onDrop) onDrop();
    a.shape = 'arrow';
    ghost.hl = true;
    yield* wait(70);
    ui.delete(ghost);
    for (const [el, m] of targets) yield* typeIn(a, el, m, { retype: true, hold: 60, per: clamp(220 / Math.max(1, m.n), 10, 26) });
    later(100, function* () { yield* fadeOut(sel, 220); });
  }

  const RUN = {
    *type(a, j, carry) { yield* typeIn(a, j.el, j.m, { carry, per: j.slow ? 52 : undefined }); },
    *write(a, j, carry) {
      const m = j.m, el = j.el, H = m.B.h, P = 40, top = m.tb.t, hgt = m.tb.b - m.tb.t;
      const pt = lx => m.map(lx, top + hgt * 0.72 + Math.sin(lx * 0.23) * hgt * 0.14);
      const [sx, sy0] = pt(m.tb.l);
      yield* moveTo(a, sx, sy0, { carry });
      a.shape = 'pen'; yield* press(a);
      show(el);
      yield* tween(clamp(m.n * 46, 340, 620), p => {
        const lx = lerp(m.tb.l - 2, m.tb.r + 3, p);
        put(el, 'clip-path', `polygon(${-P}px ${-P}px,${lx.toFixed(1)}px ${-P}px,${lx.toFixed(1)}px ${H + P}px,${-P}px ${H + P}px)`);
        [a.x, a.y] = pt(lx);
      }, inOutSine);
      release(a); back(el, 'clip-path'); a.shape = 'arrow';
    },
    *grow(a, j, carry) {
      // Type the word small, then step the font size up to the real thing.
      const m = j.m, el = j.el, fs = parseFloat(getComputedStyle(el).fontSize) || 200;
      const k0 = clamp((small ? 15 : 18) / fs, 0.03, 1);
      m.ox = 0; m.oy = 0; m.s = k0;
      put(el, 'transform-origin', '0 0'); put(el, 'scale', String(k0));
      const corners = () => { const [x0, y0] = m.map(m.tb.l, m.tb.t), [x1, y1] = m.map(m.tb.r + 2, m.tb.b); return [x0, y0, x1, y1]; };
      let [x0, y0, x1, y1] = corners();
      yield* moveTo(a, x0, y0, { carry });
      a.shape = 'ibeam';
      const sel = yield* dragBox(a, { l: x0, t: y0, r: x1, b: y1 }, { dur: 200 });
      show(el); put(el, 'clip-path', clipFor(m, 0));
      const caret = addUI({ type: 'caret', m, k: 0, color: a.color, typing: true });
      yield* typeChars(m, 40, k => { caret.k = k; const c = clipFor(m, k); if (c) put(el, 'clip-path', c); else back(el, 'clip-path'); });
      back(el, 'clip-path'); ui.delete(caret);
      a.shape = 'arrow'; sel.handles = true;
      yield* moveTo(a, x1, y1, { dur: 150 });
      yield* press(a);
      const tag = addUI({ type: 'tag', text: `Anton ${Math.round(k0 * fs)}`, x: (x0 + x1) / 2, y: y1 + 7, color: a.color, ink: a.ink });
      const steps = [24, 32, 44, 60, 80, 108, 144, 192, 256].filter(v => v < fs * 0.9).concat(fs);
      for (const v of steps) {
        const sa = m.s, sb = v / fs;
        yield* tween(56, p => {
          m.s = lerp(sa, sb, p); put(el, 'scale', m.s.toFixed(4));
          [x0, y0, x1, y1] = corners();
          Object.assign(sel, { x: x0, y: y0, w: x1 - x0, h: y1 - y0 });
          a.x = x1; a.y = y1; tag.x = (x0 + x1) / 2; tag.y = y1 + 7;
        }, out3);
        tag.text = `Anton ${Math.round(v)}`;
        yield* wait(12);
      }
      release(a);
      back(el, 'scale'); back(el, 'transform-origin');
      later(260, function* () { ui.delete(tag); yield* fadeOut(sel, 260); });
    },
    *media(a, j, carry) {
      const C = j.C, el = j.el, img = j.img;
      yield* moveTo(a, C.l, C.t, { carry });
      a.shape = 'cross';
      const sel = yield* dragBox(a, C, { measure: true, dur: clamp(220 + Math.hypot(C.w, C.h) * 0.2, 280, 440) });
      sel.handles = true; sel.badge = `${Math.round(C.w)} × ${Math.round(C.h)}`;
      put(el, 'background-color', '#141417'); put(el, 'background-image', ICON);
      put(el, 'background-repeat', 'no-repeat'); put(el, 'background-position', 'center'); put(el, 'background-size', small ? '34px' : '44px');
      a.shape = 'arrow';
      yield* moveTo(a, C.l + C.w * 0.5, C.t + C.h * 0.55, { dur: 220 });
      yield* press(a); release(a);
      if (img && j.ir) {
        put(el, 'background-image', 'none');
        show(img);
        const ir = j.ir;
        yield* tween(600, p => put(img, 'clip-path', `inset(0 0 ${Math.max(0, ir.b - lerp(C.t, C.b, p)).toFixed(1)}px 0)`), inOut3);
        back(img, 'clip-path');
      }
      put(el, 'transition', 'background-color .45s ease'); put(el, 'background-color', 'transparent');
      sel.badge = '';
      later(200, function* () {
        yield* fadeOut(sel, 240);
        yield* wait(320);
        for (const p of ['transition', 'background-color', 'background-image', 'background-repeat', 'background-position', 'background-size']) back(el, p);
      });
    },
    *button(a, j, carry) {
      const B = j.B, el = j.el, m = j.m;
      yield* moveTo(a, B.l, B.t, { carry });
      a.shape = 'cross';
      show(el); put(el, 'color', 'transparent'); put(el, 'clip-path', `inset(0 ${B.w}px ${B.h}px 0)`);
      const sel = yield* dragBox(a, B, { measure: true, each: (w, h) => put(el, 'clip-path', `inset(0 ${(B.w - w).toFixed(1)}px ${(B.h - h).toFixed(1)}px 0)`) });
      back(el, 'clip-path'); sel.handles = true;
      if (m.n) {
        const [lx, ly] = m.map(m.chars[0].l, (m.tb.t + m.tb.b) / 2);
        yield* moveTo(a, lx, ly, { dur: 220 });
        a.shape = 'ibeam'; yield* press(a); release(a);
        sel.handles = false;
        const txt = addUI({ type: 'chars', m, k: 0 });
        const caret = addUI({ type: 'caret', m, k: 0, color: j.caretColor === 'agent' ? a.color : '#ffffff', typing: true });
        yield* typeChars(m, clamp(360 / m.n, 14, 34), k => { txt.k = k; caret.k = k; });
        ui.delete(txt); ui.delete(caret);
      }
      back(el, 'color');
      a.shape = 'arrow'; sel.handles = true;
      later(160, function* () { yield* fadeOut(sel, 220); });
    },
    *shape(a, j, carry) { yield* popIn(a, j.el, { carry, B: j.B, round: true }); },
    *pop(a, j, carry) { yield* popIn(a, j.el, { carry, B: j.B }); },
    *line(a, j, carry) {
      yield* penLine(a, j.seg, carry);
      if (j.reveal) show(j.el); else back(j.el, `border-${j.side}-color`);
    },
    *seq(a, j, carry) {
      // One collaborator works through a row: its rule, number, marker, title, text.
      let c = carry;
      for (const st of j.steps) {
        if (st.k === 'type') yield* typeIn(a, st.el, st.m, { carry: c, per: st.slow ? 52 : undefined, hold: 80 });
        else if (st.k === 'line') { yield* penLine(a, st.seg, c); back(st.el, `border-${st.side}-color`); }
        else yield* popIn(a, st.el, { carry: c, B: st.B, round: st.round });
        c = false;
      }
    },
    *tidy(a, j, carry) {
      // ⌘D a few times so copies pile up, offset like Figma does, then "Tidy up" spreads them into the rows.
      const S = j.S, n = j.copies.length, off = i => 9 * (i + 1);
      yield* moveTo(a, S.l + S.w * 0.5, S.t + S.h * 0.5, { carry });
      const sel = addUI({ type: 'sel', x: S.l, y: S.t, w: S.w, h: S.h, color: a.color, ink: a.ink, handles: true });
      yield* press(a); release(a);
      const key = addUI({ type: 'key', text: '⌘ D', a, hit: 0, color: a.color });
      const ghosts = [];
      for (let i = 0; i < n; i++) {
        key.hit = 1;
        ghosts.push(addUI({ type: 'ghost', ms: j.sms, dx: off(i), dy: off(i), color: a.color, alpha: 0.9, outline: { x: S.l + off(i), y: S.t + off(i), w: S.w, h: S.h } }));
        Object.assign(sel, { x: S.l + off(i), y: S.t + off(i) });
        yield* wait(95);
      }
      ui.delete(key);
      // The "Tidy up" control under the pile.
      const tx = S.l + S.w * 0.5, ty = S.t + off(n - 1) + S.h + 16;
      const tag = addUI({ type: 'tag', text: 'Tidy up', x: tx, y: ty, color: a.color, ink: a.ink });
      yield* moveTo(a, tx, ty + 7, { dur: 220 });
      yield* press(a); release(a);
      ui.delete(tag);
      Object.assign(sel, { x: S.l, y: S.t, h: j.copies[n - 1].B.b - S.t });
      yield* tween(500, p => {
        ghosts.forEach((gh, i) => {
          const T = j.copies[i].B, e = human(clamp((p - i * 0.07) / 0.72, 0, 1));
          gh.dx = lerp(off(i), T.l - S.l, e); gh.dy = lerp(off(i), T.t - S.t, e);
          Object.assign(gh.outline, { x: S.l + gh.dx, y: S.t + gh.dy, h: lerp(S.h, T.h, e) });
        });
      }, linear);
      // Landed: each copy becomes its real row, with the first row's text waiting to be retyped.
      const marks = [];
      j.copies.forEach((c, i) => {
        ui.delete(ghosts[i]);
        back(c.row, 'border-bottom-color');
        if (c.icon) show(c.icon);
        if (c.arrow) show(c.arrow);
        for (const r of jobs) {
          if (r.kind !== 'retype' || r.copy !== j || r.at !== i || r.done) continue;
          r.stale = addUI({ type: 'ghost', ms: j.sms, dx: c.B.l - S.l, dy: c.B.t - S.t, color: a.color, alpha: 0.85 });
        }
        const prev = i ? j.copies[i - 1].B : S;
        marks.push(addUI({ type: 'gap', x0: S.l - 10, y0: prev.t, x1: S.l - 10, y1: c.B.t }));
      });
      a.shape = 'arrow';
      later(520, function* () { marks.forEach(m => spawn(fadeOut(m, 240))); yield* fadeOut(sel, 240); });
    },
    *rotate(a, j, carry) {
      // Grab the card just outside its corner and swing it to its resting tilt.
      const el = j.el, B = j.B, ccx = B.l + B.w / 2, ccy = B.t + B.h / 2, deg = j.deg || 0;
      const hx = B.r + 10, hy = B.b + 10, r = Math.hypot(hx - ccx, hy - ccy), a0 = Math.atan2(hy - ccy, hx - ccx);
      yield* moveTo(a, hx, hy, { carry });
      a.shape = 'rot';
      const sel = addUI({ type: 'rsel', cx: ccx, cy: ccy, w: B.w, h: B.h, ang: 0, color: a.color, ink: a.ink, badge: '0°', a });
      yield* press(a);
      put(el, 'rotate', '0deg');
      yield* tween(560, p => {
        const d = deg * p, rad = (d * Math.PI) / 180;
        put(el, 'rotate', `${d.toFixed(2)}deg`);
        sel.ang = rad; sel.badge = `${d.toFixed(1)}°`;
        a.x = ccx + Math.cos(a0 + rad) * r; a.y = ccy + Math.sin(a0 + rad) * r;
      }, human);
      release(a);
      // Hand the tilt back to the stylesheet; it looks identical, so the swap can't be seen.
      back(el, 'rotate'); back(el, 'transform');
      later(80, function* () { back(el, 'transition'); });
      a.shape = 'arrow';
      later(240, function* () { yield* fadeOut(sel, 240); });
    },
    *repeat(a, j, carry) {
      // Select the row, then ⌘D: each press drops a copy one slot further down,
      // still showing the first row's text until someone retypes it.
      const S = j.S;
      yield* moveTo(a, S.l + S.w * 0.55, S.t + S.h * 0.5, { carry });
      const sel = addUI({ type: 'sel', x: S.l, y: S.t, w: S.w, h: S.h, color: a.color, ink: a.ink, handles: true });
      yield* press(a); release(a);
      const key = addUI({ type: 'key', text: '⌘ D', a, hit: 0, color: a.color });
      yield* wait(140);
      let from = S;
      for (const [i, c] of j.copies.entries()) {
        const T = c.B, dy0 = from.t - S.t, DY = T.t - S.t, DX = T.l - S.l;
        key.hit = 1;
        const ghost = addUI({ type: 'ghost', ms: j.sms, dx: DX, dy: dy0, color: a.color, alpha: 0.9 });
        yield* tween(170, q => { ghost.dy = lerp(dy0, DY, q); Object.assign(sel, { y: S.t + ghost.dy, h: lerp(from.h, T.h, q) }); }, out3);
        ui.delete(ghost);
        // The copy is real now: its rule and icon, and the first row's text waiting to be retyped.
        back(c.row, 'border-top-color');
        if (c.icon) show(c.icon);
        for (const r of jobs) {
          if (r.kind !== 'retype' || r.copy !== j || r.at !== i || r.done || !j.smap[r.part]) continue;
          r.stale = addUI({ type: 'ghost', ms: [j.smap[r.part]], dx: DX, dy: DY, color: a.color, alpha: 0.85 });
        }
        from = T;
        yield* wait(120);
      }
      later(160, function* () { ui.delete(key); yield* fadeOut(sel, 220); });
    },
    *stretch(a, j, carry) {
      // "SYSTEMS THAT SCALE.": grab the word's corner, drag it bigger, let go and it springs back.
      const el = j.el, B = j.B, fs = parseFloat(getComputedStyle(el).fontSize) || 50, peak = 1.42;
      yield* moveTo(a, B.r, B.b, { carry });
      const sel = addUI({ type: 'sel', x: B.l, y: B.t, w: B.w, h: B.h, color: a.color, ink: a.ink, handles: true });
      const tag = addUI({ type: 'tag', text: `Anton ${Math.round(fs)}`, x: B.l + B.w / 2, y: B.b + 7, color: a.color, ink: a.ink });
      put(el, 'display', 'inline-block'); put(el, 'transform-origin', '0 100%');
      const size = k => {
        put(el, 'scale', k.toFixed(3));
        Object.assign(sel, { y: B.b - B.h * k, w: B.w * k, h: B.h * k });
        a.x = B.l + B.w * k; a.y = B.b; tag.x = B.l + (B.w * k) / 2;
        tag.text = `Anton ${Math.round(fs * k)}`;
      };
      yield* press(a);
      yield* tween(420, p => size(lerp(1, peak, p)), human);
      yield* wait(150);
      release(a);
      yield* tween(420, p => size(lerp(peak, 1, outBack(p))), linear);
      size(1);
      back(el, 'scale'); back(el, 'transform-origin'); back(el, 'display');
      a.shape = 'arrow';
      later(160, function* () { ui.delete(tag); yield* fadeOut(sel, 220); });
    },
    *dupchip(a, j, carry) {
      // Option-drag a copy of the first skill chip into this slot, then retype its label.
      const S = j.SB, T = j.TB, el = j.el, m = j.m;
      yield* moveTo(a, S.l + S.w * 0.5, S.t + S.h * 0.5, { carry });
      a.shape = 'dup';
      const sel = addUI({ type: 'sel', x: S.l, y: S.t, w: S.w, h: S.h, color: a.color, ink: a.ink, handles: true });
      yield* press(a);
      const ghost = addUI({ type: 'ghost', ms: [j.sm], dx: 0, dy: 0, color: a.color, chip: true, frame: { x: S.l, y: S.t, w: S.w, h: S.h } });
      const ax = a.x, ay = a.y, DX = T.l - S.l, DY = T.t - S.t;
      yield* tween(clamp(Math.hypot(DX, DY) * 0.9, 200, 420), q => {
        const dx = DX * q, dy = DY * q, w = lerp(S.w, T.w, q), h = lerp(S.h, T.h, q);
        Object.assign(ghost.frame, { x: S.l + dx, y: S.t + dy, w, h });
        ghost.dx = dx; ghost.dy = dy;
        a.x = ax + dx + (T.w - S.w) * 0.5 * q; a.y = ay + dy;
        Object.assign(sel, { x: S.l + dx, y: S.t + dy, w, h });
      }, human);
      release(a); ui.delete(ghost);
      // The copy is real now, still showing the first chip's label.
      show(el); put(el, 'color', 'transparent');
      const stale = addUI({ type: 'ghost', ms: [j.sm], dx: DX, dy: DY, color: a.color, alpha: 0.85 });
      Object.assign(sel, { x: T.l, y: T.t, w: T.w, h: T.h });
      if (m.n) {
        const [lx, ly] = m.map(m.chars[0].l, (m.tb.t + m.tb.b) / 2);
        yield* moveTo(a, lx, ly, { dur: 150 });
        a.shape = 'ibeam'; yield* press(a); release(a);
        stale.hl = true; yield* wait(90);
        ui.delete(stale);
        const txt = addUI({ type: 'chars', m, k: 0 }), caret = addUI({ type: 'caret', m, k: 0, color: a.color, typing: true });
        yield* typeChars(m, clamp(260 / m.n, 10, 28), k => { txt.k = k; caret.k = k; });
        ui.delete(txt); ui.delete(caret);
      } else ui.delete(stale);
      back(el, 'color');
      a.shape = 'arrow';
      later(100, function* () { yield* fadeOut(sel, 200); });
    },
    *dup(a, j, carry) { yield* duplicate(a, [j.sm], boxOf(j.sm), boxOf(j.m), carry, [[j.el, j.m]]); },
    *duprow(a, j, carry) {
      yield* duplicate(a, j.sms, j.SB, j.B, carry, j.parts.filter(Boolean).map(p => [p, j.mm.get(p)]), () => back(j.el, 'border-bottom-color'));
    },
    *guide(a, j, carry) {
      const s = sy(), v = j.axis === 'x', gx = v ? RULER * 0.5 : vw * rnd(0.56, 0.68), gy = s + (v ? vh * rnd(0.5, 0.6) : RULER * 0.5);
      yield* moveTo(a, gx, gy, { carry });
      a.shape = v ? 'ew' : 'ns'; yield* press(a);
      const gd = addUI({ type: 'guide', axis: j.axis, pos: v ? gx : gy, a, tag: true });
      yield* tween(clamp(Math.abs(j.pos - (v ? gx : gy)) * 0.36, 220, 420), p => {
        if (v) { a.x = lerp(gx, j.pos, p); gd.pos = a.x; } else { a.y = lerp(gy, j.pos, p); gd.pos = a.y; }
      }, human);
      release(a); gd.tag = false;
      a.shape = 'arrow';
    },
    *frame(a, j, carry) {
      // Draw the card's frame; its real background and border grow with the drag.
      const B = j.B, el = j.el;
      yield* moveTo(a, B.l, B.t, { carry });
      a.shape = 'cross';
      show(el); put(el, 'clip-path', `inset(0 ${B.w}px ${B.h}px 0)`);
      const sel = yield* dragBox(a, B, {
        measure: true, dur: clamp(200 + Math.hypot(B.w, B.h) * 0.25, 260, 440),
        each: (w, h) => put(el, 'clip-path', `inset(0 ${(B.w - w).toFixed(1)}px ${(B.h - h).toFixed(1)}px 0)`),
      });
      back(el, 'clip-path');
      if (!j.bare) placeholder(el);
      sel.handles = true; sel.badge = `${Math.round(B.w)} × ${Math.round(B.h)}`;
      a.shape = 'arrow';
      later(260, function* () { sel.badge = ''; yield* fadeOut(sel, 220); });
    },
    *sweep(a, j, carry) {
      // Drop the picture into its frame; it sweeps in top to bottom.
      const B = j.B, el = j.el, pic = j.p.pic, img = pic && $('img', pic);
      yield* moveTo(a, B.l + B.w * 0.5, B.t + B.h * 0.55, { carry });
      yield* press(a); release(a);
      for (let t = 0; img && !(img.complete && img.naturalWidth) && t < 1400;) t += yield;
      unplaceholder(el);
      if (pic && j.pr) {
        const pr = j.pr;
        show(pic);
        yield* tween(560, p => put(pic, 'clip-path', `inset(0 0 ${Math.max(0, pr.b - lerp(B.t, B.b, p)).toFixed(1)}px 0)`), inOut3);
        back(pic, 'clip-path');
      }
      if (j.p.badge) { show(j.p.badge); spawn(popScale(j.p.badge, 0.6)); }
    },
    *dupcard(a, j, carry) {
      // Option-drag a copy of the first card into this slot, then fill it in.
      const S = j.SB, T = j.TB, SC = j.SC, TC = j.TC, p = j.p;
      yield* moveTo(a, S.l + S.w * 0.5, S.t + S.h * 0.5, { carry });
      a.shape = 'dup';
      const sel = addUI({ type: 'sel', x: SC.l, y: SC.t, w: SC.w, h: SC.h, color: a.color, ink: a.ink, handles: true });
      yield* press(a);
      const ghost = addUI({ type: 'ghost', ms: j.sms, dx: 0, dy: 0, color: a.color, frame: { x: S.l, y: S.t, w: S.w, h: S.h } });
      const gap = addUI({ type: 'gap', x0: 0, y0: 0, x1: 0, y1: 0 });
      const ax = a.x, ay = a.y, DX = T.l - S.l, DY = T.t - S.t, CX = j.Tcap.l - j.Scap.l, CY = j.Tcap.t - j.Scap.t;
      const vert = Math.abs(DY) > Math.abs(DX);
      yield* tween(clamp(Math.hypot(DX, DY) * 0.7, 260, 480), q => {
        const dx = DX * q, dy = DY * q;
        Object.assign(ghost.frame, { x: S.l + dx, y: S.t + dy, w: lerp(S.w, T.w, q), h: lerp(S.h, T.h, q) });
        ghost.dx = CX * q; ghost.dy = CY * q;
        a.x = ax + dx + (T.w - S.w) * 0.5 * q; a.y = ay + dy + (T.h - S.h) * 0.5 * q;
        Object.assign(sel, { x: SC.l + (TC.l - SC.l) * q, y: SC.t + (TC.t - SC.t) * q, w: lerp(SC.w, TC.w, q), h: lerp(SC.h, TC.h, q) });
        if (vert) Object.assign(gap, { x0: SC.l - 8, y0: SC.t, x1: SC.l - 8, y1: SC.t + dy });
        else Object.assign(gap, { x0: SC.l, y0: SC.t - 8, x1: SC.l + dx, y1: SC.t - 8 });
      }, human);
      release(a); ui.delete(gap); ui.delete(ghost);
      // The copy is real now: an empty frame, the caption line and arrow, and the old text waiting to be retyped.
      show(p.frame); placeholder(p.frame);
      back(p.cap, 'border-bottom-color');
      if (p.arrow) show(p.arrow);
      for (const r of jobs) {
        if (r.kind !== 'retype' || r.copy !== j || r.done || !j.smap[r.part]) continue;
        r.stale = addUI({ type: 'ghost', ms: [j.smap[r.part]], dx: CX, dy: CY, color: a.color, alpha: 0.85 });
      }
      a.shape = 'arrow';
      later(160, function* () { yield* fadeOut(sel, 220); });
    },
    *retype(a, j, carry) { yield* typeIn(a, j.el, j.m, { retype: true, carry, stale: j.stale, hold: 60, per: clamp(220 / Math.max(1, j.m.n), 10, 26) }); },
    *stamp(a, j, carry) {
      // Stamp each logo in as the cursor runs along the rail.
      j.extra.forEach(show);
      if (!j.list.length) return;
      const f = j.list[0];
      yield* moveTo(a, f.x, f.y, { carry });
      for (const L of j.list) {
        yield* moveTo(a, L.x, L.y, { dur: 110 });
        yield* press(a); release(a);
        show(L.el); spawn(popScale(L.el, 0.5));
        yield* wait(24);
      }
    },
  };

  function pick(a) {
    let best = null, score = Infinity;
    const top = intro ? -Infinity : above();
    for (const j of jobs) {
      if (j.taken || j.deps.some(d => !d.done)) continue;
      // Scrolled past: the Work director reveals it in one step instead.
      if (j.ay != null && j.ay <= top) continue;
      // Only what's on screen gets built; the crew waits for the rest to scroll into view.
      if (!reach(j)) continue;
      const s = j.prio * 420 + Math.hypot((j.ax ?? cx) - a.x, (j.ay ?? cy) - a.y) + rnd(0, 60);
      if (s < score) { score = s; best = j; }
    }
    if (best) best.taken = true;
    return best;
  }
  function* worker(a) {
    if (opts.handoff) {
      // Already here from the section above: back to full strength, then on to the next layer.
      yield* wait(a.i * 10);
      const a0 = a.alpha, k0 = a.scale, l0 = a.label;
      if (a0 < 1 || k0 < 1) spawn(tween(220, q => { a.alpha = lerp(a0, 1, q); a.scale = lerp(k0, 1, q); }, out3));
      if (l0 > 0 && l0 < 1) spawn(tween(220, q => (a.label = lerp(l0, 1, q)), out3));
    } else yield* wait(a.i * 26);
    if (!intro && !opts.handoff) {
      // Burst out of the visitor's pointer, where the crew sank in at the end of the intro.
      const p = origin();
      a.x = p.x; a.y = p.y;
      const ang = rnd(0, Math.PI * 2), v = rnd(380, 640);
      a.vx = Math.cos(ang) * v; a.vy = Math.sin(ang) * v + (p.top ? 420 : 0);
      if (!burst) { burst = true; addUI({ type: 'ring', x: p.x, y: p.y, r0: 4, r1: 80, t: 0, dur: 480, color: '#e32b3c', a0: 0.8, w0: 2 }); }
      spawn(tween(180, q => { a.alpha = q; a.scale = lerp(0.5, 1, q); }, out3));
    }
    a.mode = 'script';
    let carry = true;
    for (;;) {
      const j = pick(a);
      if (!j) {
        if (jobs.every(x => x.done)) return;
        const s = sy();
        a.mode = 'script';
        if (intro) yield* moveTo(a, clamp(a.x + rnd(-50, 50), 30, vw - 30), clamp(a.y + rnd(-36, 36), s + 30, s + vh - 60), { dur: rnd(420, 700), carry });
        // Nothing on screen left to build: float in the lower part of the screen, over the
        // section, ready for whatever the visitor scrolls up next.
        else {
          const lo = Math.min(s + vh - 90, Math.max(s + headerH + 24, s + vh * 0.5, box(root).t + 24));
          yield* moveTo(a, clamp(a.x + rnd(-70, 70), 30, vw - 30), clamp(a.y + rnd(-40, 60), lo, s + vh - 70), { dur: rnd(650, 1050), carry });
        }
        carry = false;
        continue;
      }
      a.mode = 'script';
      if (!a.label) spawn(tween(260, p => (a.label = p), out3));
      const began = vt;
      if (j.prep) { j.prep(); j.prep = null; }
      yield* RUN[j.kind](a, j, carry);
      carry = false;
      j.done = true; done++; tally();
      if (log) log.push([a.name, j.kind, j.el ? j.el.className || j.el.tagName : j.axis, Math.round(began), Math.round(vt)]);
    }
  }
  // Reveal a layer in one step (it was scrolled past, skipped, or left waiting too long).
  function flushJob(j) {
    j.taken = j.done = true; done++;
    for (const [el, p] of j.own) back(el, p);
    if (j.kind === 'stamp' && j.extra) j.extra.forEach(show);
    if (j.stale) ui.delete(j.stale);
    if (log) log.push(['flush', j.kind, j.el ? j.el.className || j.el.tagName : j.axis, Math.round(vt), Math.round(vt)]);
  }
  function* finale() {
    html.dataset.intro = 'outro';
    count.innerHTML = '<i class="swarm-live"></i>Live';
    for (const o of [...ui]) if (o.type !== 'ring') spawn(fadeOut(o, 300));
    spawn(tween(380, p => (rul.t = 1 - p), inOut3));
    for (const el of rest) { put(el, 'transition', 'opacity .7s ease'); put(el, 'opacity', '1'); }
    // Hand over: the crew flocks to the visitor's pointer, or to the call to action, and sinks into it.
    if (!(ptr.mouse && onScreen(ptr))) {
      const cta = $('.primary-link', hero), b = cta && visible(cta) ? box(cta) : { l: cx, t: cy, w: 0, h: 0 };
      const fixed = { x: b.l + b.w / 2, y: b.t + b.h / 2 };
      goal = () => fixed;
    }
    for (const a of agents) {
      spawn(tween(220, p => (a.label = 1 - p), linear));
      a.mode = 'flock'; a.vx = rnd(-80, 80); a.vy = rnd(-80, 80);
    }
    yield* wait(400);
    pill.classList.add('off');
    for (const a of agents) {
      spawn((function* () {
        yield* wait(a.i * 16);
        a.mode = 'sink';
        yield* tween(260, p => { a.alpha = 1 - p; a.scale = 1 - 0.5 * p; }, inOut3);
      })());
    }
    yield* wait(260 + N * 16);
    const G = goal();
    addUI({ type: 'ring', x: G.x, y: G.y, r0: 6, r1: 70, t: 0, dur: 420, color: '#e32b3c', a0: 0.8, w0: 2 });
    yield* wait(380);
    finish();
  }
  function* director() {
    spawn(tween(380, p => { tile.a = p; tile.s = lerp(0.8, 1, p); }, out3));
    later(100, function* () { yield* tween(700, p => (tile.draw = p), inOut3); });
    later(120, function* () { pill.classList.add('on'); });
    // The crew joins one by one, name tags on, then gets pulled into orbit around the logo.
    agents.forEach(a => (LB[a.i] = labelSprite(a)));
    let joined = 0;
    agents.map(a => a.i).sort(() => Math.random() - 0.5).forEach((idx, k) => {
      const a = agents[idx];
      later(100 + k * 26, function* () {
        joinedEl.textContent = String(++joined);
        yield* tween(260, p => { a.alpha = Math.min(1, p * 1.8); a.scale = lerp(0.55, 1, p); a.label = Math.min(1, p * 1.4); }, outBack);
        yield* wait(120);
        a.mode = 'orbit'; a.vx = rnd(-60, 60); a.vy = rnd(-60, 60);
        yield* tween(240, p => (a.label = 1 - p), linear);
      });
    });
    later(500, function* () { count.textContent = 'Warming up'; });
    yield* until(() => (ready && vt > 1150) || vt > 2600);
    // Wind up: the orbit tightens and spins faster, then the crew bursts out.
    yield* tween(300, p => (squeeze = 1 - 0.55 * p), inOut3);
    html.dataset.intro = 'build';
    survey();
    buildRulers();
    agents.forEach(a => (LB[a.i] = labelSprite(a)));
    spawn(tween(420, p => { tile.s = lerp(1, 1.55, p); tile.a = 1 - p; }, out3));
    addUI({ type: 'flash', x: cx, y: cy, r0: 60, r1: Math.hypot(vw, vh) * 0.55, t: 0, dur: 700 });
    addUI({ type: 'ring', x: cx, y: cy, r0: 34, r1: Math.hypot(vw, vh) * 0.6, t: 0, dur: 1100, color: '#e32b3c', a0: 0.75, w0: 2.4 });
    spawn(tween(420, p => (rul.t = p), out3));
    tally();
    agents.forEach(a => spawn(worker(a)));
    yield* until(() => jobs.every(j => j.done));
    yield* wait(120);
    yield* finale();
  }
  function* workDirector() {
    const t0 = performance.now();
    survey();
    const t1 = performance.now();
    buildRulers();
    if (log) log.push(['perf', 'start', `survey ${Math.round(t1 - t0)}ms, sprites ${Math.round(performance.now() - t1)}ms, setup ${Math.round(t0 - born)}ms`, 0, 0]);
    if (opts.handoff) { const r0 = rul.t; if (r0 < 1) spawn(tween(220, p => (rul.t = lerp(r0, 1, p)), out3)); }
    else {
      later(80, function* () { pill.classList.add('on'); });
      spawn(tween(420, p => (rul.t = p), out3));
    }
    tally();
    agents.forEach(a => spawn(worker(a)));
    // Layers are built as they scroll into view, and the crew waits for the
    // visitor in between. Anything scrolled past is revealed in one step, and
    // so is everything once the section leaves the screen.
    let waiting = false;
    while (!jobs.every(j => j.done)) {
      yield;
      const R = root.getBoundingClientRect(), gone = R.bottom < headerH + 20 || R.top > vh, top = above();
      for (let moved = true; moved;) {
        moved = false;
        for (const j of jobs) {
          if (j.taken || j.done || j.deps.some(d => !d.done)) continue;
          if (gone || flushAll || (j.ay != null && j.ay <= top)) { flushJob(j); moved = true; }
        }
      }
      // When nothing on screen is left, the pill tells the visitor the crew is waiting on them.
      const now = jobs.some(j => !j.done) && !jobs.some(j => j.taken && !j.done) && !jobs.some(j => !j.taken && !j.done && !j.deps.some(d => !d.done) && reach(j));
      if (now !== waiting) { waiting = now; shown = -1; }
      if (waiting) { if (shown !== done) { shown = done; count.textContent = `${done}/${jobs.length} · scroll ↓`; } } else tally();
    }
    yield* wait(120);
    // If the visitor has already reached the next section, carry straight on into it.
    const next = nextReady();
    if (next) { handoff(next); return; }
    yield* workFinale();
  }
  // Pass the crew, as they are, to the next section's build. Both happen in one frame, so nothing blinks.
  function handoff(next) {
    const state = {
      rul: rul.t,
      agents: agents.map(a => ({ x: a.x, y: a.y, vx: a.vx, vy: a.vy, rot: a.rot, alpha: a.alpha, scale: a.scale, label: a.label, trail: a.trail, tA: a.tA, shape: 'arrow' })),
    };
    if (log) window.__swarmHandoffs = (window.__swarmHandoffs || 0) + 1;
    finish(true);
    next.start(state);
  }
  // A wait during the goodbye that breaks off if the visitor reaches the next section meanwhile.
  function* waitOrHandoff(ms) {
    for (let t = 0; t < ms;) {
      const next = nextReady();
      if (next) { handoff(next); return true; }
      t += yield;
    }
    return false;
  }
  function* workFinale() {
    // "All shipped" once the last section on the page is done.
    count.innerHTML = `<i class="swarm-live"></i>${waitingSections.length ? 'Shipped' : 'All shipped'}`;
    for (const o of [...ui]) if (o.type !== 'ring') spawn(fadeOut(o, 300));
    spawn(tween(380, p => (rul.t = 1 - p), inOut3));
    if (opts.rail) delete opts.rail.dataset.hold;
    // Section done: the crew goes home into the visitor's cursor (or the last touch on a phone).
    const touch = !ptr.mouse && onScreen(ptr.touch) ? ptr.touch : null;
    if (touch) goal = () => ({ x: touch.x, y: touch.y + sy() });
    const lead = (ptr.mouse && onScreen(ptr)) || !!touch;
    const s = sy();
    for (const a of agents) {
      spawn(tween(220, p => (a.label = 1 - p), linear));
      if (lead) { a.mode = 'flock'; a.vx = rnd(-80, 80); a.vy = rnd(-80, 80); } else {
        // Scatter off the screen, on to the next build.
        const ang = Math.atan2(a.y - (s + vh / 2), a.x - vw / 2) + rnd(-0.35, 0.35), v = rnd(260, 420);
        a.vx = Math.cos(ang) * v; a.vy = Math.sin(ang) * v; a.mode = 'exit';
      }
    }
    if (yield* waitOrHandoff(lead ? 400 : 160)) return;
    pill.classList.add('off');
    for (const a of agents) {
      spawn((function* () {
        yield* wait(a.i * (lead ? 16 : 12));
        if (lead) a.mode = 'sink';
        yield* tween(lead ? 260 : 520, p => { a.alpha = 1 - p; a.scale = 1 - 0.5 * p; }, lead ? inOut3 : out3);
      })());
    }
    if (yield* waitOrHandoff(lead ? 260 + N * 16 : 520 + N * 12)) return;
    if (lead) { const G = goal(); addUI({ type: 'ring', x: G.x, y: G.y, r0: 6, r1: 60, t: 0, dur: 420, color: '#e32b3c', a0: 0.8, w0: 2 }); }
    if (yield* waitOrHandoff(lead ? 380 : 80)) return;
    finish();
  }

  // -------------------------------------------------------------- physics
  function physics(dt) {
    if (dt <= 0) return;
    const t = vt / 1000, G = goal();
    for (const a of agents) {
      if (a.mode === 'script' || a.mode === 'idle') { if (a.trail.length) a.trail.splice(0, 2); a.tA = Math.max(0, a.tA - dt * 4); continue; }
      if (a.mode === 'sink') { const k = Math.min(1, dt * 9); a.x += (G.x - a.x) * k; a.y += (G.y - a.y) * k; }
      else if (a.mode === 'exit') { const f = 1 + dt * 2.4; a.vx *= f; a.vy *= f; a.x += a.vx * dt; a.y += a.vy * dt; }
      else {
        // Orbit a centre (the logo, or the pointer or button at the end) and keep apart.
        const fl = a.mode === 'flock', ox = fl ? G.x : cx, oy = fl ? G.y : cy;
        const dx = a.x - ox, dy = a.y - oy, d = Math.hypot(dx, dy) || 1, ux = dx / d, uy = dy / d;
        const r = a.r * (fl ? 0.3 : squeeze) * (1 + 0.16 * Math.sin(t * 1.9 + a.ph)), sp = a.w * Math.max(r, 46) * (fl ? 1 : 1 + (1 - squeeze) * 1.4);
        let tx = -uy * sp + ux * (r - d) * 3, ty = ux * sp + uy * (r - d) * 3;
        for (const b of agents) {
          if (b === a) continue;
          const ex = a.x - b.x, ey = a.y - b.y, e2 = ex * ex + ey * ey;
          if (e2 < 900 && e2 > 0.01) { const e = Math.sqrt(e2), f = ((30 - e) / 30) * 340; tx += (ex / e) * f; ty += (ey / e) * f; }
        }
        if (!fl && ptr.mouse) {
          const px = a.x - ptr.x, py = a.y - ptr.y, p2 = px * px + py * py;
          if (p2 < 16900 && p2 > 0.01) { const p = Math.sqrt(p2), f = ((130 - p) / 130) * 1100; tx += (px / p) * f; ty += (py / p) * f; }
        }
        const k = Math.min(1, dt * 4.2);
        a.vx += (tx - a.vx) * k; a.vy += (ty - a.vy) * k;
        const v = Math.hypot(a.vx, a.vy), vmax = fl ? 950 : 1300;
        if (v > vmax) { a.vx *= vmax / v; a.vy *= vmax / v; }
        a.x += a.vx * dt; a.y += a.vy * dt;
      }
      if (a.mode !== 'sink' && Math.hypot(a.vx, a.vy) > 30) a.rot += wrap(Math.atan2(a.vy, a.vx) + TURN - a.rot) * Math.min(1, dt * 12);
      a.trail.push(a.x, a.y);
      if (a.trail.length > 24) a.trail.splice(0, 2);
      a.tA = Math.min(1, a.tA + dt * 4);
    }
  }

  // ------------------------------------------------------------- drawing
  let curFont = '';
  const font = f => { if (curFont !== f) { g.font = f; curFont = f; } };
  const al = v => Math.round(v * DPR) / DPR + (DPR === 1 ? 0.5 : 0);
  const ascent = new Map();
  function drawChars(m, k, dx, dy) {
    for (let i = 0; i < k; i++) {
      const c = m.chars[i];
      if (c.space) continue;
      font(c.font);
      let A = ascent.get(c.font);
      if (A == null) { const mt = g.measureText('Hg'); A = mt.fontBoundingBoxAscent ?? mt.actualBoundingBoxAscent ?? 0; ascent.set(c.font, A); }
      g.fillStyle = c.color;
      const [x, y] = m.map(c.l, c.t);
      g.fillText(c.ch, x + dx, y + dy + A * m.s);
    }
  }
  function badge(text, x, y, bg, ink, left) {
    font(BADGE);
    const w = Math.ceil(g.measureText(text).width) + 10, h = small ? 15 : 16;
    const l = clamp(left ? x : x - w / 2, 2, vw - w - 2), t = clamp(y, view.t + 2, view.t + vh - h - 2);
    g.fillStyle = bg; rr(g, l, t, w, h, 3); g.fill();
    g.fillStyle = ink; g.textBaseline = 'middle'; g.fillText(text, l + 5, t + h / 2 + 0.5);
    g.textBaseline = 'alphabetic';
  }
  function drawUI(o) {
    const A = o.alpha ?? 1;
    if (A <= 0.003) return;
    g.globalAlpha = A;
    switch (o.type) {
      case 'guide': {
        const v = o.axis === 'x';
        let hot = false;
        for (const s of ui) if (s.type === 'sel' && Math.abs((v ? Math.min(s.x, s.x + s.w) : Math.min(s.y, s.y + s.h)) - o.pos) < 0.8) { hot = true; break; }
        g.fillStyle = GUIDE; g.globalAlpha = A * (hot ? 1 : 0.78);
        const w = hot ? 1.5 : 1;
        if (v) g.fillRect(Math.round(o.pos), view.t, w, vh); else g.fillRect(0, Math.round(o.pos), vw, w);
        g.globalAlpha = A;
        if (o.tag) badge(String(Math.round(o.pos)), o.a.x + 12, o.a.y + 12, GUIDE, '#fff', true);
        break;
      }
      case 'sel': {
        let { x, y, w, h } = o;
        if (w < 0) { x += w; w = -w; }
        if (h < 0) { y += h; h = -h; }
        g.strokeStyle = o.color; g.lineWidth = 1;
        if (o.ellipse && w > 2 && h > 2) { g.beginPath(); g.ellipse(x + w / 2, y + h / 2, w / 2, h / 2, 0, 0, Math.PI * 2); g.stroke(); g.globalAlpha = A * 0.35; }
        g.strokeRect(al(x), al(y), Math.round(w), Math.round(h));
        g.globalAlpha = A;
        if (o.measure) {
          g.save(); g.setLineDash([2, 3]); g.globalAlpha = A * 0.65;
          const mx = al(x + w), my = al(y + h), edge = RULER * rul.t;
          g.beginPath(); g.moveTo(mx, view.t + edge); g.lineTo(mx, y + h); g.moveTo(edge, my); g.lineTo(x + w, my); g.stroke();
          g.restore();
        }
        if (o.handles) {
          g.fillStyle = '#fff';
          for (const [hx, hy] of [[x, y], [x + w, y], [x, y + h], [x + w, y + h]]) { g.fillRect(Math.round(hx) - 3, Math.round(hy) - 3, 6, 6); g.strokeRect(al(hx - 3), al(hy - 3), 5, 5); }
        }
        if (o.badge) badge(o.badge, x + w / 2, y + h + 7, o.color, o.ink);
        break;
      }
      case 'caret': {
        if (!o.typing && Math.floor(vt / 480) % 2) break;
        const m = o.m, c = o.k > 0 ? m.chars[o.k - 1] : m.chars[0];
        if (!c) break;
        const lx = o.k > 0 ? c.r : c.l, [x0, y0] = m.map(lx, c.t), [x1, y1] = m.map(lx, c.b);
        g.strokeStyle = o.color; g.lineWidth = clamp((c.b - c.t) * m.s * 0.045, 1.2, 3);
        g.beginPath(); g.moveTo(x0, y0); g.lineTo(x1, y1); g.stroke();
        break;
      }
      case 'pen': {
        g.strokeStyle = o.color; g.lineWidth = 1.5;
        g.beginPath(); g.moveTo(o.x0, o.y0); g.lineTo(o.x1, o.y1); g.stroke();
        g.fillStyle = '#fff'; g.lineWidth = 1;
        const sq = (x, y) => { g.fillRect(x - 3, y - 3, 6, 6); g.strokeRect(x - 3, y - 3, 6, 6); };
        sq(o.x0, o.y0);
        if (o.closed) sq(o.x1, o.y1);
        break;
      }
      case 'ghost': {
        if (o.frame && o.chip) {
          // A copied skill chip is just its outline.
          const f = o.frame;
          g.globalAlpha = A * 0.9; g.strokeStyle = '#5a4447'; g.lineWidth = 1;
          g.strokeRect(al(f.x), al(f.y), Math.round(f.w), Math.round(f.h));
          g.globalAlpha = A;
        } else if (o.frame) {
          // The copied card's empty frame, with its placeholder glyph.
          const f = o.frame;
          g.globalAlpha = A * 0.9; g.fillStyle = '#131010'; g.fillRect(f.x, f.y, f.w, f.h);
          g.strokeStyle = '#3a2b2d'; g.lineWidth = 1; g.strokeRect(al(f.x), al(f.y), Math.round(f.w), Math.round(f.h));
          const s = small ? 22 : 30, ix = f.x + f.w / 2 - s / 2, iy = f.y + f.h / 2 - s / 2;
          g.strokeStyle = '#56565e'; g.lineWidth = 1.2;
          g.strokeRect(ix + s * 0.15, iy + s * 0.19, s * 0.7, s * 0.62);
          g.beginPath(); g.arc(ix + s * 0.38, iy + s * 0.42, s * 0.07, 0, Math.PI * 2);
          g.moveTo(ix + s * 0.19, iy + s * 0.73); g.lineTo(ix + s * 0.4, iy + s * 0.54); g.lineTo(ix + s * 0.54, iy + s * 0.66); g.lineTo(ix + s * 0.65, iy + s * 0.58); g.lineTo(ix + s * 0.82, iy + s * 0.72);
          g.stroke();
          g.globalAlpha = A;
        }
        if (o.outline) {
          const f = o.outline;
          g.strokeStyle = o.color; g.lineWidth = 1; g.globalAlpha = A * 0.55;
          g.strokeRect(al(f.x), al(f.y), Math.round(f.w), Math.round(f.h));
          g.globalAlpha = A;
        }
        for (const m of o.ms) {
          if (o.hl) {
            g.fillStyle = o.color; g.globalAlpha = A * 0.32;
            for (const L of m.lines) { const [lx, ly] = m.map(L.l, L.t); g.fillRect(lx + o.dx, ly + o.dy, (L.r - L.l) * m.s, (L.b - L.t) * m.s); }
          }
          g.globalAlpha = A * 0.92;
          drawChars(m, m.n, o.dx, o.dy);
        }
        break;
      }
      case 'chars': drawChars(o.m, o.k, 0, 0); break;
      case 'gap': {
        const { x0, y0, x1, y1 } = o, vert = Math.abs(x1 - x0) < Math.abs(y1 - y0), len = Math.hypot(x1 - x0, y1 - y0);
        if (len < 4) break;
        g.strokeStyle = GUIDE; g.lineWidth = 1; g.beginPath(); g.moveTo(x0, y0); g.lineTo(x1, y1);
        if (vert) { g.moveTo(x0 - 3, y0); g.lineTo(x0 + 3, y0); g.moveTo(x1 - 3, y1); g.lineTo(x1 + 3, y1); }
        else { g.moveTo(x0, y0 - 3); g.lineTo(x0, y0 + 3); g.moveTo(x1, y1 - 3); g.lineTo(x1, y1 + 3); }
        g.stroke();
        badge(String(Math.round(len)), vert ? x0 - 30 : (x0 + x1) / 2, vert ? (y0 + y1) / 2 - 8 : y0 - 19, GUIDE, '#fff', vert);
        break;
      }
      case 'tag': badge(o.text, o.x, o.y, o.color, o.ink); break;
      case 'rsel': {
        // A rotated selection: the box turns with the object, handles on its corners.
        g.save();
        g.translate(o.cx, o.cy); g.rotate(o.ang);
        g.strokeStyle = o.color; g.lineWidth = 1;
        g.strokeRect(-o.w / 2, -o.h / 2, o.w, o.h);
        g.fillStyle = '#fff';
        for (const [hx, hy] of [[-1, -1], [1, -1], [-1, 1], [1, 1]]) { g.fillRect(hx * o.w / 2 - 3, hy * o.h / 2 - 3, 6, 6); g.strokeRect(hx * o.w / 2 - 3, hy * o.h / 2 - 3, 6, 6); }
        g.restore();
        if (o.badge && o.a) badge(o.badge, o.a.x + 14, o.a.y + 16, o.color, o.ink, true);
        break;
      }
      case 'key': {
        // A keycap riding next to the cursor; it dips when "pressed".
        if (o.hit > 0) o.hit = Math.max(0, o.hit - frameDt / 110);
        font(BADGE);
        const tw = g.measureText(o.text).width, w = Math.ceil(tw) + 16, h = small ? 20 : 22, d = o.hit > 0 ? 2 : 0;
        const l = clamp(o.a.x + 16, 2, vw - w - 2), t = clamp(o.a.y - h - 14, view.t + 2, view.t + vh - h - 2);
        g.fillStyle = '#0c0c0e'; rr(g, l, t + 3, w, h, 5); g.fill();
        g.fillStyle = o.hit > 0 ? '#202026' : '#2a2a31'; rr(g, l, t + d, w, h, 5); g.fill();
        g.strokeStyle = o.color; g.lineWidth = 1; rr(g, l + 0.5, t + d + 0.5, w - 1, h - 1, 5); g.stroke();
        g.fillStyle = '#ffffff'; g.textBaseline = 'middle'; g.fillText(o.text, l + 8, t + d + h / 2 + 0.5);
        g.textBaseline = 'alphabetic';
        break;
      }
      case 'flash': {
        o.t += frameDt;
        const p = Math.min(1, o.t / o.dur), R = lerp(o.r0, o.r1, out3(p));
        const gr = g.createRadialGradient(o.x, o.y, 0, o.x, o.y, R);
        gr.addColorStop(0, `rgba(227,43,60,${(0.3 * (1 - p)).toFixed(3)})`);
        gr.addColorStop(1, 'rgba(227,43,60,0)');
        g.fillStyle = gr; g.fillRect(o.x - R, o.y - R, R * 2, R * 2);
        if (p >= 1) ui.delete(o);
        break;
      }
      case 'ring': {
        o.t += frameDt;
        const p = Math.min(1, o.t / o.dur);
        g.strokeStyle = o.color; g.globalAlpha = (1 - p) * o.a0; g.lineWidth = lerp(o.w0, 0.4, p);
        g.beginPath(); g.arc(o.x, o.y, lerp(o.r0, o.r1, out3(p)), 0, Math.PI * 2); g.stroke();
        if (p >= 1) ui.delete(o);
        break;
      }
    }
    g.globalAlpha = 1;
  }
  // Rulers are fixed to the screen. The left one is drawn live so its numbers follow the page.
  function drawRulers() {
    if (rul.t <= 0.002 || !rul.top) return;
    const o = -RULER * (1 - rul.t), s = view.t;
    g.drawImage(rul.top, 0, o, vw, RULER);
    g.fillStyle = 'rgba(14,14,16,.97)'; g.fillRect(o, 0, RULER, vh);
    g.fillStyle = '#2a2a2f'; g.fillRect(o + RULER - 1, 0, 1, vh);
    g.fillStyle = '#3d3d44';
    for (let y = Math.ceil(s / 10) * 10; y <= s + vh; y += 10) { const h = y % 100 === 0 ? RULER * 0.55 : y % 50 === 0 ? 5 : 3; g.fillRect(o + RULER - 1 - h, y - s, h, 1); }
    font(TINY); g.fillStyle = '#6e6e76'; g.textBaseline = 'top';
    for (let y = Math.ceil((s + 1) / 100) * 100; y < s + vh; y += 100) { g.save(); g.translate(o + (small ? 2 : 3), y - s - 3); g.rotate(-Math.PI / 2); g.fillText(String(y), 0, 0); g.restore(); }
    for (const sl of ui) {
      if (sl.type !== 'sel' || !sl.measure) continue;
      let { x, y, w, h } = sl;
      if (w < 0) { x += w; w = -w; }
      if (h < 0) { y += h; h = -h; }
      const A = sl.alpha ?? 1;
      g.globalAlpha = A * 0.3; g.fillStyle = sl.color;
      g.fillRect(x, o, w, RULER - 1); g.fillRect(o, y - s, RULER - 1, h);
      g.globalAlpha = A; font(TINY); g.textBaseline = 'top';
      const a = String(Math.round(x)), b = String(Math.round(x + w));
      g.fillText(a, x + 2, o + (small ? 2 : 3));
      if (w > 40) g.fillText(b, x + w - g.measureText(b).width - 2, o + (small ? 2 : 3));
    }
    g.textBaseline = 'alphabetic';
    g.globalAlpha = 1;
    g.fillStyle = '#0e0e10'; g.fillRect(o, o, RULER, RULER);
  }
  function drawTile() {
    if (tile.a <= 0.003 || !tile.spr) return;
    const S = small ? 52 : 62, k = (S / 64) * 0.8;
    g.save();
    g.globalAlpha = tile.a; g.translate(cx, cy); g.scale(tile.s, tile.s);
    g.drawImage(tile.spr.c, -tile.spr.w / 2, -tile.spr.w / 2, tile.spr.w, tile.spr.w);
    g.scale(k, k); g.translate(-31.8, -34.5);
    g.strokeStyle = '#e02232'; g.lineWidth = 9.5; g.setLineDash([150, 150]); g.lineDashOffset = 150 * (1 - tile.draw);
    g.stroke(tile.path);
    g.restore();
  }
  function drawAgents() {
    for (const a of agents) {
      if (a.alpha <= 0.003) continue;
      const tr = a.trail, n = tr.length >> 1;
      if (a.tA > 0.02 && n > 2) {
        g.strokeStyle = a.color; g.lineWidth = 2; g.lineCap = 'round';
        const fade = a.mode === 'orbit' ? 0.5 : 0.3;
        for (let k = 1; k < n; k++) {
          g.globalAlpha = a.alpha * a.tA * (k / n) * fade;
          g.beginPath(); g.moveTo(tr[2 * k - 2], tr[2 * k - 1]); g.lineTo(tr[2 * k], tr[2 * k + 1]); g.stroke();
        }
        g.lineCap = 'butt';
      }
      const sp = spriteOf(a, a.shape), sc = a.scale * (a.down ? 0.88 : 1);
      g.globalAlpha = a.alpha;
      if (a.rot || sc !== 1) { g.save(); g.translate(a.x, a.y); g.rotate(a.rot); g.scale(sc, sc); g.drawImage(sp.c, -sp.hx, -sp.hy, SPR, SPR); g.restore(); }
      else g.drawImage(sp.c, Math.round((a.x - sp.hx) * DPR) / DPR, Math.round((a.y - sp.hy) * DPR) / DPR, SPR, SPR);
      // Name tags are drawn the first time they show.
      const L = LB[a.i] || (a.label > 0.003 ? (LB[a.i] = labelSprite(a)) : null);
      if (L && a.label > 0.003) { g.globalAlpha = a.alpha * a.label; g.drawImage(L.c, a.x + 10, a.y + 14 + (1 - a.label) * 5, L.w, L.h); }
    }
    g.globalAlpha = 1;
  }
  function render() {
    const s = sy();
    view.t = s;
    g.setTransform(1, 0, 0, 1, 0, 0);
    g.clearRect(0, 0, cv.width, cv.height);
    g.setTransform(DPR, 0, 0, DPR, 0, -s * DPR);
    for (const o of ui) if (o.type === 'guide') drawUI(o);
    for (const o of ui) if (o.type !== 'guide') drawUI(o);
    g.setTransform(DPR, 0, 0, DPR, 0, 0);
    drawRulers();
    g.setTransform(DPR, 0, 0, DPR, 0, -s * DPR);
    drawTile();
    drawAgents();
  }

  // ----------------------------------------------------------- lifecycle
  // Only time spent animating counts toward the safety cut-off, so a tab opened
  // in the background still plays the intro when the visitor switches to it.
  let alive = 0;
  function frame(now) {
    if (over) return;
    raf = requestAnimationFrame(frame);
    try {
      const dt = last ? Math.min(now - last, 34) : 16.7;
      last = now;
      alive += dt;
      if (dbg) { dbg.vt = vt; if (dbg.pause != null && vt >= dbg.pause) { frameDt = 0; render(); return; } }
      frameDt = dt * speed; vt += frameDt;
      const list = tasks.splice(0);
      for (const it of list) if (!it.next(frameDt).done) tasks.push(it);
      physics(frameDt / 1000);
      if (!over) render();
      // Safety cut-off. The Work crew may wait a while for the visitor to scroll, so it gets longer.
      if (!dbg && alive > (intro ? 20000 : 120000)) finish();
    } catch (err) {
      console.error('[swarm]', err);
      finish();
    }
  }
  function skip(hard) {
    if (over) return;
    if (speed > 1) { if (hard) finish(); return; }
    speed = 5;
    if (!intro) flushAll = true;
    if (html.dataset.intro !== 'outro') count.textContent = 'Fast-forward';
  }
  const onKey = e => {
    const k = e.key;
    if (!intro) { if (k === 'Escape') skip(true); return; }
    if (k === 'Escape' || k === 'Enter' || k === ' ' || k === 'Tab' || k === 'Home' || k === 'End' || k.startsWith('Arrow') || k.startsWith('Page')) {
      if (k !== 'Tab') e.preventDefault();
      skip(k === 'Escape');
    }
  };
  const onWheel = e => { e.preventDefault(); skip(false); };
  const onTouch = e => { e.preventDefault(); skip(false); };
  const onDown = () => skip(false);
  const onResize = () => { if (Math.abs(innerWidth - vw) > 2 || Math.abs(innerHeight - vh) > 140) finish(); };
  addEventListener('keydown', onKey, true);
  addEventListener('resize', onResize);
  if (intro) {
    addEventListener('wheel', onWheel, { passive: false });
    layer.addEventListener('touchmove', onTouch, { passive: false });
    layer.addEventListener('pointerdown', onDown);
  }
  pill.querySelector('button').addEventListener('click', () => skip(true));
  // If the visitor filters the grid mid-build, the cards change under the crew: finish at once.
  const grid = !intro && $('.project-grid', root);
  const mo = grid ? new MutationObserver(() => finish()) : null;
  if (mo) mo.observe(grid, { childList: true });

  // `handingOff` is true when the crew carries on into the next section instead of leaving.
  function finish(handingOff) {
    if (over) return;
    over = true;
    cancelAnimationFrame(raf);
    for (const [el, r] of saved) for (const p in r) back(el, p);
    saved.clear();
    removeEventListener('keydown', onKey, true);
    removeEventListener('resize', onResize);
    removeEventListener('wheel', onWheel);
    if (mo) mo.disconnect();
    layer.remove(); pill.remove(); style.remove();
    if (intro) {
      delete html.dataset.intro;
      try { history.scrollRestoration = 'auto'; } catch {}
      dispatchEvent(new Event('swarm:intro-done'));
    } else {
      delete html.dataset.build;
      if (opts.rail) delete opts.rail.dataset.hold;
      if (opts.onDone) opts.onDone();
      // The next section in line may be waiting for this crew to finish.
      if (handingOff !== true) dispatchEvent(new Event('swarm:build-done'));
    }
  }

  if (intro) {
    const img = media && $('img', media.el);
    const fonts = document.fonts
      ? Promise.all(['400 100px Anton', '400 40px Caveat', '400 12px Manrope', '600 12px Manrope', '700 12px Manrope', '800 12px Manrope'].map(f => document.fonts.load(f).catch(() => {})))
      : Promise.resolve();
    const picture = img && img.decode ? img.decode().catch(() => {}) : Promise.resolve();
    Promise.race([Promise.all([fonts, picture]), new Promise(r => setTimeout(r, 3000))]).then(() => { ready = true; });
  }

  spawn(intro ? director() : workDirector());
  // A crew that was handed over is drawn right away, in the same frame the old one goes.
  if (opts.handoff) render();
  raf = requestAnimationFrame(frame);
  return { skip: () => skip(true), stop: () => finish() };
}
